import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { useCategories } from '@/hooks/useCategories'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useOfflineCapture } from '@/lib/offline/useOfflineCapture'
import { OfflineOrdersTray } from '@/components/counter/OfflineOrdersTray'
import { OfflineHeldTicket } from '@/components/counter/OfflineHeldTicket'
import type { OfflineOrder } from '@/lib/offline/offlineStore'
import type { OfflineRefusal } from '@/lib/offline/capability'
import {
  PROXY_UNREACHABLE_CODE,
  PROXY_UNREACHABLE_STATUS,
  useOnlineAndReachable,
} from '@/lib/offline/reachability'

/**
 * F-FE-N30: turn a capability refusal into something a cashier can act on.
 *
 * "Offline" alone is useless mid-service — each of these has a different fix,
 * and the cashier is the one who has to apply it.
 */
/**
 * Dashboard-stream events that change which sales are open, and therefore the
 * Open Orders strip. A guest edit (order_updated) is handled separately — it
 * refreshes the rail, not the strip membership.
 */
const ORDER_STRIP_EVENTS = new Set([
  'order_created',
  'order_completed',
  'order_cancelled',
  'order_voided',
  'payment',
])

function offlineHintForRefusal(reason?: OfflineRefusal): string {
  switch (reason) {
    case 'needs_desktop_app':
      return 'Offline sales need the Bhookly Retail desktop app — a browser tab cannot hold them.'
    case 'no_reserved_numbers':
      return 'Offline sale numbers are used up. Reconnect briefly to reserve more.'
    default:
      return "Offline — your order is held on this device. It'll place when you're back online."
  }
}
import { CounterPaymentPanel } from '@/components/counter/CounterPaymentPanel'
import { CounterOrderTypeToggle } from '@/components/counter/CounterOrderTypeToggle'
import { TicketHeader } from '@/components/counter/rail/TicketHeader'
import { CounterRailToolbar } from '@/components/counter/rail/CounterRailToolbar'
import { CounterLoyaltyChip } from '@/components/counter/CounterLoyaltyChip'
import { useModuleVisibility } from '@/hooks/useModuleVisibility'
import {
  buildOpenOrdersStrip,
  getTicketLifecycle,
  isOpenOrdersStripMember,
  orderPayableRemaining,
} from '@/components/counter/rail/ticketState'
import { SentItemsSection } from '@/components/counter/rail/SentItemsSection'
import { UnsentItemsSection } from '@/components/counter/rail/UnsentItemsSection'
import { CashTenderPad } from '@/components/counter/rail/CashTenderPad'
import { ActionFooter } from '@/components/counter/rail/ActionFooter'
import { useBlockRestart } from '@/lib/restartGuard'
import {
  getDraftUserId,
  readDraft,
  clearDraft,
  useDraftAutosave,
  restoreDraftItems,
  type DraftFields,
} from '@/components/counter/useCounterDraft'
import {
  CounterCheckoutSuccessOverlay,
  type CheckoutCelebrationMode,
} from '@/components/counter/CounterCheckoutSuccessOverlay'
import { toGuestDateInputValue } from '@/components/counter/CounterGuestDetailsSection'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Link, useNavigate } from '@tanstack/react-router'
import { useCounterHotkeys } from '@/hooks/useCounterHotkeys'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { useOnline } from '@/hooks/useOnline'
import { getOrderTypePricing, useEnabledOrderTypes } from '@/hooks/useEnabledOrderTypes'
import type { CounterOrderTypeOption } from '@/components/counter/CounterOrderTypeToggle'
import { colorForOrderTypeId, displayLabelForOrderType, normalizeOrderTypeId, sanitizeOrderTypeHexColor } from '@/lib/orderTypeSettings'
import { computeCartTotals, mergePricingSettings } from '@/lib/counterPricing'
import { resolveScanMatch, canAddToCart } from '@/lib/barcodeScan'
import { useDashboardStream, type DashboardStreamEvent } from '@/lib/dashboardStream'
import { isTerminalOrderStatus, ORDER_NOT_EDITABLE } from '@/lib/orderStatus'
import { customerPhoneShapeError } from '@/lib/customerPhone'
import { cn } from '@/lib/utils'
import { useCurrency } from '@/contexts/CurrencyContext'
import { toastHelpers } from '@/lib/toast-helpers'
import {
  checkoutStoredIntentToReceiptPaymentMethod,
  getCashierNameFromStorage,
  getServerNameFromOrder,
  parseReceiptSettings,
  printCustomerReceipt,
  printSplitGuestReceipt,
  printUnpaidCustomerBill,
  type CustomerReceiptSettings,
} from '@/lib/printCustomerReceipt'
import {
  SplitBillDialog,
  type PaySplitGuestArgs,
} from '@/components/counter/split/SplitBillDialog'
import type { GuestShare } from '@/lib/splitBilling'
import { printGuestReceiptForOrderId } from '@/lib/printGuestReceiptReplay'
import { buildReceiptLoyaltyBlock } from '@/lib/buildReceiptLoyaltyBlock'
import { buildReceiptThankYouBlock } from '@/lib/buildReceiptThankYouBlock'
import { printPraTaxInvoice } from '@/lib/printPraTaxInvoice'
import { Skeleton } from '@/components/ui/skeleton'
import { PraInvoicePromptModal } from '@/components/counter/PraInvoicePromptModal'
import { PreviousDayBlockerModal } from '@/components/counter/PreviousDayBlockerModal'
import { StartTheDayBanner } from '@/components/counter/StartTheDayBanner'
import { OpenDayDialog } from '@/components/admin/dayclose/OpenDayDialog'
import { DailyClosingBanner } from '@/components/counter/DailyClosingBanner'
import { ManagerHandoffModal } from '@/components/counter/ManagerHandoffModal'
import { PinEntryModal } from '@/components/counter/PinEntryModal'
import { ApiClientError } from '@/api/client'
import { ProductImagePlaceholder } from '@/components/menu/ProductImagePlaceholder'
import { MenuCategoryBar } from '@/components/menu/MenuCategoryBar'
import { categoryColor, categorySurfaceStyle } from '@/lib/menuCategoryStyle'
import {
  Search,
  GripVertical,
  BadgeDollarSign,
} from 'lucide-react'
import { format } from 'date-fns'
import type {
  Product,
  Category,
  CustomerQuickAddress,
  Order,
  OrderItem,
  CreateOrderRequest,
  ProcessPaymentRequest,
} from '@/types'

interface CartItem {
  product: Product
  quantity: number
  special_instructions?: string
}

const ORDER_TYPE_TAB_DESCRIPTIONS: Record<string, string> = {
  dine_in: 'Tables & tabs',
  takeout: 'Pickup orders',
  delivery: 'Driver handoff',
}

// orderPayableRemaining + lifecycle helpers live in rail/ticketState

const COUNTER_CHECKOUT_RAIL_KEY = 'pos-counter-checkout-rail-px'
const COUNTER_RAIL_DEFAULT = 448
const COUNTER_RAIL_MIN = 300

function readCheckoutRailWidth(): number {
  if (typeof window === 'undefined') return COUNTER_RAIL_DEFAULT
  const raw = localStorage.getItem(COUNTER_CHECKOUT_RAIL_KEY)
  const n = raw ? parseInt(raw, 10) : NaN
  return Number.isFinite(n) && n >= COUNTER_RAIL_MIN ? n : COUNTER_RAIL_DEFAULT
}

// Saved height of the old resizable categories pane. Categories now render as
// a fixed single-row chip bar, so the key is cleared on mount.
const LEGACY_CATEGORY_PANE_KEY = 'pos-counter-category-pane-px'

export function CounterInterface() {
  const [checkoutOpen, setCheckoutOpen] = useState(false)
  const [orderType, setOrderType] = useState<string>('dine_in')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [customerName, setCustomerName] = useState('')
  const [customerEmail, setCustomerEmail] = useState('')
  const [customerPhone, setCustomerPhone] = useState('')
  const [guestBirthday, setGuestBirthday] = useState('')
  // Delivery-only. deliveryFeeOverride null = use the settings default; a
  // number replaces it in the live total and is persisted with the order.
  const [deliveryAddress, setDeliveryAddress] = useState('')
  const [deliveryFeeOverride, setDeliveryFeeOverride] = useState<number | null>(null)
  // Saved addresses of the last-picked customer (F-FE-N29). Lives here (not in
  // the guest popover, which unmounts on close) so the default can still fill
  // the delivery field when the cashier picks the customer FIRST and switches
  // the order type to delivery afterwards.
  const [customerSavedAddresses, setCustomerSavedAddresses] = useState<CustomerQuickAddress[]>([])
  const [cart, setCart] = useState<CartItem[]>([])
  const [orderNotes, setOrderNotes] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [createCheckoutIntent, setCreateCheckoutIntent] = useState<'cash' | 'card' | 'online'>('cash')

  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null)
  const [paymentCheckoutIntent, setPaymentCheckoutIntent] = useState<'cash' | 'card' | 'online'>('cash')

  // F-MM-N7: `onPrimaryPay` awaits an in-flight tender switch, so anything it
  // reads afterwards must not come from the closure captured at tap time —
  // that snapshot predates the re-price. These refs always hold the latest.
  const selectedOrderRef = useRef<Order | null>(null)
  selectedOrderRef.current = selectedOrder
  const paymentCheckoutIntentRef = useRef<'cash' | 'card' | 'online'>('cash')
  paymentCheckoutIntentRef.current = paymentCheckoutIntent
  const [cashReceived, setCashReceived] = useState('')
  const [cashModalOpen, setCashModalOpen] = useState(false)
  // Selected online provider (Easypaisa/JazzCash/…), migration 050. Shown inline
  // in the payment panel when Online is selected and the tenant has active
  // sub-methods; the Charge CTA requires a pick.
  const [selectedOnlineSub, setSelectedOnlineSub] = useState<string | null>(null)
  // Split bill (F-FE-N19). The session itself lives on the order
  // (payOrder.split_state) — this only controls the dialog's visibility.
  const [splitDialogOpen, setSplitDialogOpen] = useState(false)
  const [discountMode, setDiscountMode] = useState<'amount' | 'percent'>('amount')
  const [discountValue, setDiscountValue] = useState('')
  // Triggered when the backend returns 409 previous_day_open — counter
  // staff cannot ring sales until yesterday's day is closed.
  const [previousDayBlocker, setPreviousDayBlocker] = useState<{
    open: boolean
    message?: string
  }>({ open: false })

  // F-CTRL-N32: backend returns 409 `day_not_open` when nobody has started
  // today. Auto-open was removed — a business day now begins only when a human
  // states what's in the drawer. Counter staff can do this themselves, so we
  // raise the Start-the-day dialog inline rather than sending them to find a
  // manager. The cart is untouched; they retry the sale after opening.
  const [startDayBlocker, setStartDayBlocker] = useState<{
    open: boolean
    message?: string
  }>({ open: false })

  // Manager-PIN handoff modal — opened by the Daily Closing banner when
  // a cashier-tier session needs to hand off to a manager.
  const [handoffOpen, setHandoffOpen] = useState(false)

  // Whether the logged-in user can navigate to /admin/day-close. Only
  // admin and manager have that permission; for plain counter staff we
  // surface a "ask your manager" message instead of a CTA.
  const userCanRunDayClose = useMemo(() => {
    try {
      const raw = localStorage.getItem('pos_user')
      if (!raw) return false
      const u = JSON.parse(raw) as { role?: string }
      return u.role === 'admin' || u.role === 'manager'
    } catch {
      return false
    }
  }, [])

  // Raw role string for the banner so it can render either the
  // "Close now" (manager) or "Get manager" (cashier) CTA.
  const userRole = useMemo(() => {
    try {
      const raw = localStorage.getItem('pos_user')
      if (!raw) return ''
      const u = JSON.parse(raw) as { role?: string }
      return u.role ?? ''
    } catch {
      return ''
    }
  }, [])

  // Shared connectivity signal (proactive UX only — the authoritative failure
  // signal is ApiClientError.isNetworkError in the submit path). Drives the
  // offline place-order guard, the honest hint, and the reconnect nudge.
  // F-FE-N30: the OS signal alone is not enough. With the router up and the ISP
  // down — the outage restaurants actually have — navigator.onLine stays true,
  // so every `!online` branch below (dine-in table select, the fire button, the
  // capture path) silently never fired. Treat the terminal as connected only
  // when the backend has also actually answered.
  const osOnline = useOnline()
  const { backendReachable } = useOnlineAndReachable()
  const online = osOnline && backendReachable
  // Per-cashier key for the local order draft (data-loss guard). Null if
  // pos_user is unreadable — persistence then no-ops rather than writing an
  // un-namespaced key another cashier could inherit.
  const draftUserId = useMemo(() => getDraftUserId(), [])

  /**
   * Context for the post-payment PRA tax invoice prompt. When non-null, the
   * PraInvoicePromptModal is shown; the captured order/settings/payment are
   * reused to build the second slip without re-querying the server.
   */
  const [praPromptContext, setPraPromptContext] = useState<{
    order: Order
    settings: CustomerReceiptSettings
    paymentMethod: string
    paidAt: Date
  } | null>(null)
  const [praPrinting, setPraPrinting] = useState(false)
  /** Full-screen thank-you after a completed payment (customer-facing terminal). */
  const [checkoutCelebration, setCheckoutCelebration] = useState<CheckoutCelebrationMode | null>(null)
  const [historyReadOnlyOrder, setHistoryReadOnlyOrder] = useState<Order | null>(null)
  const [provisionalPrintBusy, setProvisionalPrintBusy] = useState(false)
  const [historyGuestPrintBusy, setHistoryGuestPrintBusy] = useState(false)
  /** When set, cart adds lines to this existing table order (occupied table flow). */
  const [continuingOrderId, setContinuingOrderId] = useState<string | null>(null)
  /**
   * F-FE-N31: the offline-held order the cart is appending to.
   *
   * Deliberately separate from continuingOrderId, which names an order that
   * EXISTS server-side. This one lives only in this device's queue: there is
   * nothing to fetch, nothing to pay, and the append is a local mutation that
   * replays later as part of a single create.
   */
  const [offlineTarget, setOfflineTarget] = useState<OfflineOrder | null>(null)
  /**
   * Bumped after an append so the tray re-reads immediately. The pending COUNT
   * is unchanged by an append — without this nonce the row's item count stayed
   * stale until the next 5s poll, which reads as "the round didn't land".
   */
  const [offlineTrayNonce, setOfflineTrayNonce] = useState(0)
  /** Full order data when continuing an existing order (includes items already sent to kitchen). */
  const [existingOrder, setExistingOrder] = useState<Order | null>(null)
  /** Brief highlight on product tile after a successful add-to-cart tap. */
  const [lastTappedProductId, setLastTappedProductId] = useState<string | null>(null)
  const tapFlashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Per-tap nonce — drives the "+1" pop animation on the product card by being
  // included in the rendered <span>'s React key, so every increment forces a
  // remount + fresh keyframe re-fire (no state-cleanup timer needed because
  // the CSS animation ends at opacity 0 via `forwards`).
  const [tapPop, setTapPop] = useState<{ productId: string; nonce: number }>({
    productId: '',
    nonce: 0,
  })
  /** Brief highlight on cart row after add / quantity bump (slightly longer than product tile). */
  const [lastCartFlashProductId, setLastCartFlashProductId] = useState<string | null>(null)
  const cartFlashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const cartRowRefs = useRef<Record<string, HTMLTableRowElement | null>>({})
  const [cartLiveAnnouncement, setCartLiveAnnouncement] = useState<{ text: string; id: number }>({ text: '', id: 0 })
  const [existingItemsExpanded, setExistingItemsExpanded] = useState(false)
  const [discardCartConfirmOpen, setDiscardCartConfirmOpen] = useState(false)
  /**
   * Set when a tender switch off 'card' was refused because a bank card
   * promo is applied (409 card_promo_requires_card_tender). The dialog
   * offers "remove the promo and switch" — the cashier makes the money
   * decision, never this component.
   */
  const [cardPromoTenderConflict, setCardPromoTenderConflict] = useState<{
    orderId: string
    intent: 'cash' | 'card' | 'online'
  } | null>(null)
  const [voidModal, setVoidModal] = useState<{
    orderId: string; itemId: string; itemName: string; qty: number; unitPrice: number
  } | null>(null)
  /**
   * Inline banner shown above the remaining-balance row inside the checkout
   * dialog after a partial payment is recorded. Replaces a toast that would
   * otherwise float away from the operator's point of focus.
   */
  const [partialPaymentBanner, setPartialPaymentBanner] = useState<string | null>(null)

  const [checkoutRailPx, setCheckoutRailPx] = useState(readCheckoutRailWidth)
  /** Below md (768px): toggle between menu browser and ticket/checkout rail.
   *  From md (768px) up we show the side-by-side split + drag-to-resize handle;
   *  phones (<768px) keep the cleaner tabbed UI. The split can feel cramped on
   *  ~768–900px, which is an accepted tradeoff for resizable tablets.
   */
  const [mobileWorkspaceTab, setMobileWorkspaceTab] = useState<'menu' | 'ticket'>(
    'menu',
  )
  // Keep this in sync with the `md:` split-layout classes in the render below.
  const isSplitLayout = useMediaQuery('(min-width: 768px)')
  const counterSplitRef = useRef<HTMLDivElement>(null)
  const railDragRef = useRef<{ startX: number; startW: number; maxW: number } | null>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const railScrollRef = useRef<HTMLDivElement>(null)

  const queryClient = useQueryClient()
  // Loyalty / promo surfaces follow the Marketing Suite (ENABLED_MODULES).
  const marketingOn = useModuleVisibility().isEnabled('marketing')
  const { formatCurrency } = useCurrency()

  const { enabledIds: enabledOrderTypeIds, raw: orderTypesRaw } = useEnabledOrderTypes()

  const counterOrderToggleOptions = useMemo<CounterOrderTypeOption[]>(() => {
    return orderTypesRaw.map((r) => ({
      id: r.id,
      label: (r.label && r.label.trim()) || r.id,
      description:
        ORDER_TYPE_TAB_DESCRIPTIONS[normalizeOrderTypeId(r.id)] ?? 'Counter checkout',
      color: sanitizeOrderTypeHexColor(r.color, '#64748b'),
    }))
  }, [orderTypesRaw])

  const firstEnabledOrderTypeId = useMemo(() => {
    const row = orderTypesRaw.find((r) => enabledOrderTypeIds.has(normalizeOrderTypeId(r.id)))
    return row?.id ?? 'dine_in'
  }, [orderTypesRaw, enabledOrderTypeIds])

  const createOrderPricingExtras = useMemo(
    () => getOrderTypePricing(orderType, orderTypesRaw),
    [orderType, orderTypesRaw]
  )

  // Live delivery fee on the not-yet-placed ticket: a manual override when set,
  // otherwise the settings default for the order type.
  const effectiveDeliveryFee =
    orderType === 'delivery' && deliveryFeeOverride != null
      ? deliveryFeeOverride
      : createOrderPricingExtras.deliveryFee

  // Delivery orders can't be placed without a phone + address. The server
  // enforces this too (delivery_details_required); the gate keeps the cashier
  // off the 400 and points them at the Guest details popover.
  const deliveryDetailsMissing =
    orderType === 'delivery' && (!customerPhone.trim() || !deliveryAddress.trim())

  // A typed guest phone becomes a customers row on the server, so it has to
  // look like a phone number. Same gate shape as delivery details: stop the
  // cashier here rather than at the 400, and only when a new order is being
  // created (continuing an existing tab doesn't re-resolve the customer).
  const guestPhoneError = customerPhoneShapeError(customerPhone)

  // If an admin disables the currently-selected order type mid-session,
  // auto-switch to the first enabled type and clear transient session state
  // (customer, checkout) so the cashier isn't stuck on a hidden tab.
  // Mirrors the reset logic in CounterOrderTypeToggle's onChange handler below.
  useEffect(() => {
    if (enabledOrderTypeIds.size === 0) return
    if (enabledOrderTypeIds.has(normalizeOrderTypeId(orderType))) return
    setOrderType(firstEnabledOrderTypeId)
    setCheckoutOpen(false)
    setSelectedOrder(null)
    // Guest fields deliberately survive an order-type change (F-FE-N29): the
    // cashier just captured them and re-searching the customer on every type
    // switch was real counter friction. They still clear on order completion.
    setDeliveryAddress('')
    setDeliveryFeeOverride(null)
  }, [enabledOrderTypeIds, orderType, firstEnabledOrderTypeId])

  // F-FE-N29: fill the delivery field from the picked customer's saved default
  // when the order BECOMES a delivery. The pick-time prefill (guest popover)
  // only fires when the order is already a delivery; this covers the cashier
  // who attaches the customer first and switches the order type after. Only
  // when the field is empty — never clobber a typed address, and deliberately
  // not re-triggered by the cashier clearing the field.
  useEffect(() => {
    if (orderType !== 'delivery') return
    if (customerSavedAddresses.length === 0) return
    const def = customerSavedAddresses.find((a) => a.is_default) ?? customerSavedAddresses[0]
    setDeliveryAddress((cur) => (cur.trim() ? cur : def.address))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderType, customerSavedAddresses])

  const dismissCheckoutCelebration = useCallback(() => {
    setCheckoutCelebration(null)
  }, [])

  // Mark this route so the shadcn ToastViewport repositions to top-center
  // (see index.css -> body[data-counter-route="true"]). The default bottom-right
  // viewport would otherwise overlap the Totals + primary CTA on the checkout rail.
  useEffect(() => {
    if (typeof document === 'undefined') return
    document.body.setAttribute('data-counter-route', 'true')
    return () => {
      document.body.removeAttribute('data-counter-route')
    }
  }, [])

  // Clear the partial-payment affirmation whenever the checkout dialog closes
  // or the operator switches to a different order so a stale banner never
  // leaks across tickets.
  useEffect(() => {
    if (!checkoutOpen) setPartialPaymentBanner(null)
  }, [checkoutOpen, selectedOrder?.id])

  // Open-orders freshness across terminals: a sale settled or parked on
  // another device broadcasts on the dashboard stream.
  //
  // A guest attach for the order currently open on the rail — e.g. from a
  // staff phone on /scan — re-reads that order so the rail's LOCAL guest
  // fields update too. The ref (not state) keeps this callback's identity
  // stable for the stream hook.
  const activeRailOrderIdRef = useRef<string | null>(null)
  const handleOrderStream = useCallback(
    (ev: DashboardStreamEvent) => {
      if (ev.type === 'order_updated') {
        queryClient.invalidateQueries({ queryKey: ['counterPendingOrders'] })
        const activeId = activeRailOrderIdRef.current
        if (ev.extra?.change === 'guest' && activeId && ev.order_id === activeId) {
          void apiClient.getOrder(activeId).then((res) => {
            // Guard against the rail having moved to another order meanwhile.
            if (!res.success || !res.data || activeRailOrderIdRef.current !== activeId) return
            const o = res.data
            setCustomerName(o.customer_name ?? '')
            setCustomerEmail(o.customer_email ?? '')
            setCustomerPhone(o.customer_phone ?? '')
            setGuestBirthday(toGuestDateInputValue(o.guest_birthday))
          })
        }
        return
      }
      if (!ORDER_STRIP_EVENTS.has(ev.type)) return
      queryClient.invalidateQueries({ queryKey: ['counterPendingOrders'] })
    },
    [queryClient],
  )
  const streamStatus = useDashboardStream({ onEvent: handleOrderStream })

  useEffect(() => {
    return () => {
      if (tapFlashTimeoutRef.current != null) {
        clearTimeout(tapFlashTimeoutRef.current)
        tapFlashTimeoutRef.current = null
      }
      if (cartFlashTimeoutRef.current != null) {
        clearTimeout(cartFlashTimeoutRef.current)
        cartFlashTimeoutRef.current = null
      }
    }
  }, [])

  const { data: categories = [] } = useCategories()

  // The whole catalogue, once. Category is a client-side view over this list,
  // NOT a separate fetch.
  //
  // It used to be keyed on selectedCategory, which made the category the data
  // SOURCE rather than a filter — so with "Matcha" selected the cashier's search
  // box could only ever see those 5 items, and typing "brownie" returned
  // nothing. It also meant a network round-trip on every category tap.
  //
  // Fetching everything is cheap here: the largest catalogue in the fleet is
  // ~200 products. getAllProducts() walks every page — /products is capped at
  // 100/page, and a single call silently truncated the menu on bigger tenants.
  const { data: products = [] } = useQuery({
    queryKey: ['products', 'all'],
    queryFn: () => apiClient.getAllProducts(),
  })

  // --- Local order-draft persistence (data-loss guard) -----------------------
  // Persist the NEW-order draft (only while continuingOrderId == null — once it
  // becomes a server-side order it's no longer a draft) so a reload/crash/outage
  // doesn't vaporize a staged cart. Does NOT enable offline selling.
  const draftFields: DraftFields = useMemo(
    () => ({
      orderType,
      cart: cart.map((i) => ({
        productId: i.product.id,
        quantity: i.quantity,
        special_instructions: i.special_instructions,
      })),
      orderNotes,
      customerName,
      customerEmail,
      customerPhone,
      guestBirthday,
      deliveryAddress,
      deliveryFeeOverride,
    }),
    [
      orderType,
      cart,
      orderNotes,
      customerName,
      customerEmail,
      customerPhone,
      guestBirthday,
      deliveryAddress,
      deliveryFeeOverride,
    ],
  )
  // Only a NEW order's cart is a draft worth restoring. When the cart is
  // destined for a ticket that already exists — server-side or held offline —
  // saving it would resurrect that round onto a fresh order after a restart.
  useDraftAutosave(draftUserId, draftFields, continuingOrderId == null && offlineTarget == null)

  // Restore once, on mount, only into an empty new-order slate, and only after
  // the menu has loaded so items reconcile against live products/prices.
  const draftRestoreAttemptedRef = useRef(false)
  useEffect(() => {
    if (draftRestoreAttemptedRef.current || !draftUserId) return
    if (continuingOrderId != null || cart.length > 0) return
    // Reconcile against the FULL product list. `products` is now always the
    // full catalogue (category is a client-side view), so the old
    // category-dependent fallback is gone.
    const fullProducts = queryClient.getQueryData<Product[]>(['products', 'all']) ?? products
    if (!fullProducts || fullProducts.length === 0) return // wait for the menu
    draftRestoreAttemptedRef.current = true
    const draft = readDraft(draftUserId)
    if (!draft || draft.cart.length === 0) return
    const { items, dropped } = restoreDraftItems(draft.cart, fullProducts)
    if (items.length === 0) {
      clearDraft(draftUserId)
      return
    }
    setCart(items)
    setOrderType(draft.orderType || 'dine_in')
    setOrderNotes(draft.orderNotes ?? '')
    setCustomerName(draft.customerName ?? '')
    setCustomerEmail(draft.customerEmail ?? '')
    setCustomerPhone(draft.customerPhone ?? '')
    setGuestBirthday(draft.guestBirthday ?? '')
    setDeliveryAddress(draft.deliveryAddress ?? '')
    setDeliveryFeeOverride(draft.deliveryFeeOverride ?? null)
    toastHelpers.info(
      'Held order restored',
      dropped > 0
        ? `Your staged order is back — ${dropped} item${dropped > 1 ? 's' : ''} removed (no longer available).`
        : 'Your staged order is back.',
    )
  }, [draftUserId, continuingOrderId, cart.length, products, queryClient])

  // Reconnect nudge: when connectivity returns and a held new-order draft is
  // still staged, invite a manual place (single tap on the re-enabled CTA). We
  // never auto-fire — there's no idempotency key, so an auto-resubmit after a
  // flapping connection could duplicate the order.
  const prevOnlineRef = useRef(online)
  useEffect(() => {
    const was = prevOnlineRef.current
    prevOnlineRef.current = online
    if (!was && online && continuingOrderId == null && cart.length > 0) {
      toastHelpers.info('Back online', 'You can place your held order now.')
    }
  }, [online, continuingOrderId, cart.length])
  // --- end order-draft persistence -------------------------------------------

  // Daily Closing banner data. Reuses the same React Query key the
  // AdminSidebar polls (1-min refetch) so we share cache and don't
  // double the request load.
  // Counter-scope endpoint (/day-ops/current, admin+manager+counter) rather
  // than the /admin one (admin+manager). This previously 403'd for cashier
  // sessions, so `currentDay` was permanently null for them and DailyClosingBanner
  // never rendered — despite having a "Get manager" CTA written for exactly that
  // role. Same handler, same payload, same cache key; only the gate differs.
  const { data: dayOpsCurrentRes, isPending: dayOpsCurrentPending } = useQuery({
    queryKey: ['day-ops', 'current'],
    queryFn: () => apiClient.getDayOpsCurrentCounter(),
    staleTime: 30_000,
    refetchInterval: 60_000,
  })
  const currentDay = dayOpsCurrentRes?.data ?? null

  // F-FE-N30 — offline order capture.
  //
  // The business date comes from the SERVER (day-ops/current), never from the
  // browser clock: day_boundary_hour is per-tenant, so at 2am on a 5am-boundary
  // tenant the calendar date and the business date disagree. Deriving it here
  // would rotate the reserved number block at midnight and orphan the numbers
  // already printed on tickets.
  //
  // The value survives an outage because TanStack Query persists to IndexedDB.
  // If it is somehow unavailable the empty string matches no stored pool, so
  // capture fails closed rather than issuing a number against the wrong day.
  const offlineBusinessDate = currentDay?.business_date ?? ''
  const offlineCapture = useOfflineCapture({ online, todayISO: offlineBusinessDate })


  /**
   * Reset the composer after an offline capture. Mirrors the online success
   * path so the two feel identical to the cashier — and clears the draft, since
   * the order now lives in the offline queue rather than as an unplaced cart.
   *
   * `keepTable` is set when a held order stays loaded for its next round
   * (F-FE-N31): dropping the table there would print the following round's slip
   */
  const clearOfflineCapturedCart = useCallback(() => {
    setCart([])
    setOrderNotes('')
    clearDraft(draftUserId)
  }, [draftUserId])

  // Report the outcome of the automatic post-reconnect drain. The unreconciled
  // count gets its own warning rather than being folded into the success line —
  // those items were fired to the kitchen and could NOT be billed, so they need
  // a human, not a green tick.
  useEffect(() => {
    offlineCapture.onReplayComplete((s) => {
      if (s.synced > 0) {
        // F-CTRL-N37: the synced orders now exist server-side, but every query
        // holding them is still showing the pre-outage snapshot — so the strip
        // stayed empty until the cashier reloaded by hand, which nobody in a
        // service would think to do. Refresh the surfaces the orders land on.
        queryClient.invalidateQueries({ queryKey: ['orders'] })
        queryClient.invalidateQueries({ queryKey: ['counterPendingOrders'] })
        toastHelpers.success('Back online', `${s.synced} offline sale(s) synced.`)
      }
      if (s.unreconciled > 0) {
        toastHelpers.warning(
          'Needs attention',
          `${s.unreconciled} offline item(s) were sent to the kitchen but could not be billed.`,
        )
      }
    })
  }, [offlineCapture])

  const { data: closeoutHourSetting } = useQuery({
    queryKey: ['settings', 'day_boundary_hour'],
    queryFn: () => apiClient.getSetting('day_boundary_hour'),
    staleTime: 5 * 60_000,
  })
  const closeoutHour = useMemo(() => {
    const raw = closeoutHourSetting?.data
    const n = typeof raw === 'number' ? raw : Number(raw)
    return Number.isFinite(n) && n >= 0 && n <= 23 ? n : 5
  }, [closeoutHourSetting])

  const navigate = useNavigate()

  // Tenant oversell policy. The server enforces it too — this is so the
  // cashier finds out before the customer is waiting, not after.
  const { data: blockOversellRaw } = useQuery({
    queryKey: ['setting', 'inventory.block_oversell'],
    queryFn: () => apiClient.getSetting('inventory.block_oversell'),
    staleTime: 5 * 60_000,
  })
  const blockOversell =
    blockOversellRaw?.data === true || String(blockOversellRaw?.data ?? '') === 'true'

  const { data: pricingRaw } = useQuery({
    queryKey: ['counterPricing'],
    queryFn: () => apiClient.getCounterPricing().then((r) => (r.success && r.data ? r.data : null)),
  })

  const pricing = useMemo(() => mergePricingSettings(pricingRaw), [pricingRaw])

  // Is a promo redemption active on the currently-open ticket? Used to
  // disable the per-line discount affordance with a helpful tooltip,
  // so cashiers see why before the server returns 409 conflict_promo.
  const activeRailOrderId = existingOrder?.id ?? selectedOrder?.id ?? null
  // Latest-value ref for the stream handler (declared above with it) — the
  // callback must stay identity-stable, so it reads this instead of state.
  activeRailOrderIdRef.current = activeRailOrderId
  // Cache the raw APIResponse envelope (read `.data` below), matching the other
  // two consumers of this key — CounterLoyaltyChip and CounterPromoCodeBlock,
  // which render on the same open ticket and read `.data`. Unwrapping here
  // poisoned the shared cache so those two saw "no promo applied" (→ double-apply
  // → 409 conflict_promo). Same bug class as F-FE-N6.
  const { data: activePromoRes } = useQuery({
    queryKey: ['counter-applied-promo', activeRailOrderId],
    queryFn: () =>
      activeRailOrderId
        ? apiClient.getAppliedPromo(activeRailOrderId)
        : Promise.resolve(null),
    enabled: Boolean(activeRailOrderId),
    staleTime: 5_000,
  })
  const hasActivePromo = Boolean(activePromoRes?.data?.promo_code_id)

  // Applied bank card promo on the open ticket — drives the "HBL Card
  // Discount (20%)" label on the bill totals + receipt and disables the
  // per-line discount affordance (mutex, same as promo codes). Shares the
  // query key with CounterCardPromoBlock, so cache stays consistent.
  const { data: activeCardPromoRes } = useQuery({
    queryKey: ['counter-applied-card-promo', activeRailOrderId],
    queryFn: () =>
      activeRailOrderId
        ? apiClient.getAppliedCardPromo(activeRailOrderId)
        : Promise.resolve(null),
    enabled: Boolean(activeRailOrderId),
    staleTime: 5_000,
  })
  const activeCardPromo = activeCardPromoRes?.data
  const hasActiveCardPromo = Boolean(activeCardPromo?.applied)
  const activeCardPromoLabel = hasActiveCardPromo ? activeCardPromo?.label ?? null : null

  const { data: pendingOrders = [], isLoading: pendingOrdersLoading } = useQuery({
    queryKey: ['counterPendingOrders'],
    queryFn: async () => {
      // open_only=true: server returns non-terminal rows only, so a flood of
      // completed takeaways cannot push older open dine-in bills out of the
      // last-N page (P1 — OPEN_ORDERS_STRIP_HIDDEN_2026-07-23).
      const r = await apiClient.getOrders({ per_page: 100, open_only: true })
      const list = Array.isArray(r.data) ? r.data : []
      return list.filter((o) => isOpenOrdersStripMember(o))
    },
    // Match tables: SSE is primary; poll so a dead stream cannot freeze the
    // strip for a whole shift while the floor still updates.
    refetchInterval: streamStatus === 'live' ? 60_000 : 15_000,
  })

  const ordersToCloseStrip = useMemo(
    () => buildOpenOrdersStrip(pendingOrders, orderType),
    [orderType, pendingOrders]
  )

  // The strip only shows orders of the active order type, so its surface is
  // tinted with that type's configured color (light purple under Delivery,
  // etc.) to make the tab→rail relationship visible. The amber dot/label stay
  // as the constant "needs attention" cue; amber is also the fallback surface
  // when the order type has no color configured.
  const railAccent = colorForOrderTypeId(orderTypesRaw, orderType)
  const railSurfaceStyle = railAccent
    ? { background: `color-mix(in srgb, ${railAccent} 7%, var(--card))` }
    : undefined

  const { data: paymentOrderDetail, isFetching: paymentOrderFetching } = useQuery({
    queryKey: ['order', selectedOrder?.id, 'payment-panel'],
    queryFn: async () => {
      const r = await apiClient.getOrder(selectedOrder!.id)
      if (!r.success || !r.data) throw new Error(r.message || 'Failed to load order')
      return r.data
    },
    enabled: Boolean(selectedOrder && checkoutOpen),
  })

  const orderForPayment = paymentOrderDetail ?? selectedOrder ?? null
  /** Resolved order for payment panel (always defined when an order is selected). */
  const payOrder = selectedOrder ? (orderForPayment ?? selectedOrder) : null
  /**
   * The order the payment panel is actually rendering, readable from inside an
   * async handler (F-MM-N10). `onPrimaryPay` awaits a tender switch before it
   * sends, so it needs a ref rather than the render-time closure — and it needs
   * THIS copy, the one the Charge button's figure is drawn from.
   */
  const payOrderRef = useRef<Order | null>(null)
  payOrderRef.current = payOrder

  const submitCartMutation = useMutation({
    mutationFn: async (): Promise<{
      orderId: string
      mode: 'new' | 'continue'
      itemCount: number
    }> => {
      const lines = cart.map((item) => ({
        product_id: item.product.id,
        quantity: item.quantity,
        special_instructions: item.special_instructions,
      }))
      const itemCount = cart.reduce((sum, item) => sum + item.quantity, 0)
      if (continuingOrderId) {
        const res = await apiClient.addItemsToOrder(continuingOrderId, lines)
        if (!res.success) {
          // F-CTRL-N30 backstop. The server refused because the order reached a
          // terminal state (409 order_not_editable) — almost always VoidItem's
          // auto-cancel firing while this terminal still had the order loaded.
          // Drop the dead reference and KEEP the cart, so the operator just taps
          // again and the items land on a fresh order instead of being retyped.
          if (res.error === ORDER_NOT_EDITABLE) {
            setContinuingOrderId(null)
            setExistingOrder(null)
            setSelectedOrder(null)
            throw new Error(
              'That order was already closed, so nothing was added. Your items are still in the cart — tap again to ring them up as a new order.'
            )
          }
          throw new Error(res.message || 'Could not add items to order')
        }
        return { orderId: continuingOrderId, mode: 'continue', itemCount }
      }
      const orderData: CreateOrderRequest = {
        customer_name: customerName.trim() || undefined,
        customer_email: customerEmail.trim() || undefined,
        customer_phone: customerPhone.trim() || undefined,
        guest_birthday: guestBirthday.trim() || undefined,
        order_type: orderType,
        items: lines,
        notes: orderNotes || undefined,
        delivery_address:
          orderType === 'delivery' ? deliveryAddress.trim() || undefined : undefined,
        delivery_fee_override:
          orderType === 'delivery' && deliveryFeeOverride != null
            ? deliveryFeeOverride
            : undefined,
      }
      const res = await apiClient.createCounterOrder(orderData)
      if (!res.success || !res.data?.id) {
        throw new Error(res.message || 'Could not create order')
      }
      return { orderId: res.data.id, mode: 'new', itemCount }
    },
    onSuccess: async (result) => {
      setCart([])
      setOrderNotes('')
      clearDraft(draftUserId)
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['counterPendingOrders'] })
      // Adding items to an existing order re-derives any applied card promo
      // server-side (subtotal changed) — refresh its state.
      if (result.mode === 'continue') {
        void refreshCardPromoAfterCartEdit(result.orderId)
      }

      const ref = await apiClient.getOrder(result.orderId)
      if (ref.success && ref.data && ref.data.status !== 'completed' && ref.data.status !== 'cancelled') {
        const od = ref.data
        setExistingOrder(od)
        setContinuingOrderId(od.id)
        // Keep checkout / payment rail in sync with fired items (was stale until full refresh).
        setSelectedOrder((prev) => (prev?.id === od.id ? od : prev))
        queryClient.setQueryData(['order', od.id, 'payment-panel'], od)
        setExistingItemsExpanded(false)
        setCustomerName(od.customer_name ?? '')
        setCustomerEmail(od.customer_email ?? '')
        setCustomerPhone(od.customer_phone ?? '')
        setGuestBirthday(toGuestDateInputValue(od.guest_birthday))
        setDeliveryAddress(od.delivery_address ?? '')
        setDeliveryFeeOverride(od.delivery_fee_override ?? null)
      }
    },
    onError: (err: Error) => {
      // The tenant refuses to sell below zero and the count moved between the
      // tile render and this submit (another till, a stock correction). The
      // cart is kept — the cashier fixes the quantity, they don't start over.
      if (err instanceof ApiClientError && err.code === 'insufficient_stock') {
        toastHelpers.warning('Not enough stock', err.message)
        void queryClient.invalidateQueries({ queryKey: ['products', 'all'] })
        return
      }
      if (err instanceof ApiClientError && err.code === 'previous_day_open') {
        setPreviousDayBlocker({ open: true, message: err.message })
        return
      }
      if (err instanceof ApiClientError && err.code === 'day_not_open') {
        setStartDayBlocker({ open: true, message: err.message })
        return
      }
      // Transport failure: keep the cart (it's also persisted locally) and never
      // auto-retry — there's no idempotency key, so a machine resubmit could
      // duplicate the order. Distinguish "never sent" from "sent, unknown".
      if (err instanceof ApiClientError && err.isNetworkError) {
        if (err.axiosCode === 'ECONNABORTED') {
          toastHelpers.warning(
            "Couldn't confirm your order",
            'We lost the connection after sending. Check the Orders list / kitchen before placing again to avoid a duplicate.',
          )
          queryClient.invalidateQueries({ queryKey: ['orders'] })
          queryClient.invalidateQueries({ queryKey: ['counterPendingOrders'] })
          return
        }
        // F-FE-N30: the request never left. If this terminal can capture
        // offline, do it now rather than telling the cashier to wait — this is
        // the FIRST failure that reveals the outage, and until it happened the
        // predicate had no way to know the backend was gone.
        if (offlineCapture.capability.ok) {
          void handleOfflineSubmitCart()
          return
        }
        toastHelpers.warning(
          'Offline',
          'No connection — your order is still held. It will place when you reconnect.',
        )
        return
      }
      // F-FE-N30: the desktop shell answers with a real 502 when it cannot
      // reach the tenant, so an outage arrives as an HTTP error rather than a
      // transport failure. Same recovery — the order is not lost to a toast.
      if (
        err instanceof ApiClientError &&
        err.status === PROXY_UNREACHABLE_STATUS &&
        err.code === PROXY_UNREACHABLE_CODE &&
        offlineCapture.capability.ok
      ) {
        void handleOfflineSubmitCart()
        return
      }
      toastHelpers.error('Order', err.message || 'Could not create order')
    },
  })

  /**
   * Print one guest's split slip (F-FE-N20). Best-effort: a print hiccup
   * must never block the payment flow — the money is already recorded.
   */
  const printSplitGuestSlip = useCallback(
    async (
      orderId: string,
      order: Order | null,
      share: GuestShare,
      guestNo: number,
      guestCount: number,
      paymentMethod: string
    ) => {
      try {
        let o = order
        if (!o) {
          const res = await apiClient.getOrder(orderId)
          if (res.success && res.data) o = res.data
        }
        if (!o) return
        const settingsRes = await queryClient.fetchQuery({
          queryKey: ['settings', 'all'],
          queryFn: () => apiClient.getAllSettings(),
        })
        if (!settingsRes.success || !settingsRes.data) return
        const cfg = parseReceiptSettings(settingsRes.data as Record<string, unknown>)
        const { printed } = printSplitGuestReceipt(o, cfg, share, {
          cashierName: getCashierNameFromStorage(),
          paymentMethod,
          paidAt: new Date(),
          guestNo,
          guestCount,
          orderTypeLabel: displayLabelForOrderType(o.order_type, orderTypesRaw),
          formatAmount: formatCurrency,
        })
        if (!printed) {
          toastHelpers.error('Split slip', 'Popup blocked — allow popups for this site')
        }
      } catch {
        /* slip is optional */
      }
    },
    [queryClient, formatCurrency]
  )

  /**
   * Pull the order from the server and push it into BOTH copies the till reads
   * from: the `['order', id, 'payment-panel']` query the rail renders, and the
   * `selectedOrder` / `existingOrder` state the pay tap reads its amount from.
   *
   * F-MM-N10: those two used to be able to disagree. Invalidating only the
   * query refreshed what the cashier SAW while `selectedOrder` kept the old
   * total, so the Charge button showed the new figure and the tap sent the old
   * one — and the backend rejected it with a bare `amount_exceeds_balance`.
   * Every path that rewrites totals server-side must land here or mirror it.
   */
  const refreshOrderIntoTill = useCallback(
    async (orderId: string) => {
      queryClient.invalidateQueries({ queryKey: ['counterPendingOrders'] })
      try {
        const fresh = await apiClient.getOrder(orderId)
        if (fresh.success && fresh.data) {
          const od = fresh.data
          queryClient.setQueryData(['order', orderId, 'payment-panel'], od)
          setSelectedOrder((prev) => (prev?.id === od.id ? od : prev))
          setExistingOrder((prev) => (prev?.id === od.id ? od : prev))
          return
        }
      } catch {
        /* fall through to a plain invalidation */
      }
      queryClient.invalidateQueries({ queryKey: ['order', orderId, 'payment-panel'] })
    },
    [queryClient]
  )

  const processPaymentMutation = useMutation({
    mutationFn: async ({
      orderId,
      paymentData,
    }: {
      orderId: string
      paymentData: ProcessPaymentRequest
      /** Present when this tender settles a split guest — drives the guest
       *  slip + suppresses the standard partial-payment receipt/banner. */
      splitPrint?: { share: GuestShare; guestNo: number; guestCount: number }
    }) => {
      const res = await apiClient.processCounterPayment(orderId, paymentData)
      if (!res.success) {
        throw new Error(res.message || 'Payment could not be processed')
      }
      return res
    },
    onError: (err: Error) => {
      if (err instanceof ApiClientError && err.code === 'previous_day_open') {
        setPreviousDayBlocker({ open: true, message: err.message })
        return
      }
      if (err instanceof ApiClientError && err.code === 'day_not_open') {
        setStartDayBlocker({ open: true, message: err.message })
        return
      }
      if (err instanceof ApiClientError && err.code === 'split_tender_intent_locked') {
        toastHelpers.error(
          'Split bill',
          'This bill is locked to the card tax rate — a cash tender would change it. Void the recorded payments and restart the split as mixed-tender.'
        )
        return
      }
      if (err instanceof ApiClientError && err.code === 'card_promo_requires_card_tender') {
        toastHelpers.error(
          'Bank card promo',
          'This bill has a bank card promo — it must be settled by card. Remove the promo to take another tender.'
        )
        return
      }
      if (err instanceof ApiClientError && err.code === 'split_guest_already_paid') {
        toastHelpers.error('Split bill', 'This guest was already settled on another terminal.')
        if (selectedOrder) {
          queryClient.invalidateQueries({ queryKey: ['order', selectedOrder.id, 'payment-panel'] })
        }
        return
      }
      // F-MM-N7: the order was re-priced under the operator — either the tender
      // changed or an admin edited a tax / service-charge rate while the ticket
      // sat open. The backend refused rather than bank the stale figure (which,
      // when the new total is HIGHER, used to be accepted as a silent part
      // payment). Pull the corrected order so the panel redraws on the real
      // number, and say plainly what happened instead of showing a raw code.
      if (err instanceof ApiClientError && err.code === 'order_reprice_conflict') {
        if (selectedOrder) {
          queryClient.invalidateQueries({ queryKey: ['order', selectedOrder.id, 'payment-panel'] })
          queryClient.invalidateQueries({ queryKey: ['counterPendingOrders'] })
        }
        toastHelpers.error(
          'Price changed',
          err.message || 'This order was re-priced. Check the new total and take the payment again.'
        )
        return
      }
      // F-MM-N10: the till sent a figure the order no longer owes. The usual
      // cause is a cached copy that missed a server-side total rewrite (a bank
      // card promo or promo code applied on the rail). The backend now names
      // both figures; pull the corrected order into every store so the panel
      // and the next tap agree, instead of leaving the cashier on a raw code.
      if (err instanceof ApiClientError && err.code === 'amount_exceeds_balance') {
        if (selectedOrder) void refreshOrderIntoTill(selectedOrder.id)
        toastHelpers.error(
          'Amount changed',
          err.message ||
            'The amount sent is more than this order still owes. The total was refreshed — take the payment again.'
        )
        return
      }
      toastHelpers.error('Payment', err.message || 'Payment failed')
    },
    onSuccess: async (_data, variables) => {
      let orderAfter: Order | null = null
      try {
        const orderRes = await apiClient.getOrder(variables.orderId)
        if (orderRes.success && orderRes.data) orderAfter = orderRes.data
      } catch {
        /* order fetch optional for branching */
      }

      // If we cannot refetch, assume check closed (payment API already succeeded).
      const fullyPaid = orderAfter == null ? true : orderAfter.status === 'completed'

      // Split guest settled: print their slip now (before the standard final
      // receipt on the last tender) — each guest walks away with their share.
      if (variables.splitPrint) {
        await printSplitGuestSlip(
          variables.orderId,
          orderAfter,
          variables.splitPrint.share,
          variables.splitPrint.guestNo,
          variables.splitPrint.guestCount,
          variables.paymentData.payment_method
        )
      }

      if (fullyPaid) {
        setSplitDialogOpen(false)
        setCheckoutCelebration(orderType)
        setPartialPaymentBanner(null)
        setSelectedOrder(null)
        setCheckoutOpen(false)
        setCashModalOpen(false)
        setCashReceived('')
        setDiscountValue('')
        setExistingOrder(null)
        setContinuingOrderId(null)
        setCustomerName('')
        setCustomerEmail('')
        setCustomerPhone('')
        setGuestBirthday('')
        setCustomerSavedAddresses([])
        setDeliveryAddress('')
        setDeliveryFeeOverride(null)
        setCart([])
        clearDraft(draftUserId)
        setExistingItemsExpanded(false)
        queryClient.removeQueries({ queryKey: ['order', variables.orderId, 'payment-panel'] })
      } else {
        // Inline banner inside the checkout dialog (above the remaining-balance
        // ledger) rather than a floating toast that pulls the operator's eye
        // away from the payment rail. Split tenders skip the banner — the
        // split dialog owns the "N of M guests paid" progress display.
        setPartialPaymentBanner(
          variables.splitPrint
            ? null
            : orderAfter
              ? 'Remaining balance still due on this check.'
              : 'Refresh the order if totals look wrong.'
        )
        setCashModalOpen(false)
        setCashReceived('')
        if (orderAfter) {
          setSelectedOrder(orderAfter)
          setExistingOrder((prev) => (prev?.id === orderAfter.id ? orderAfter : prev))
          queryClient.setQueryData(['order', variables.orderId, 'payment-panel'], orderAfter)
          setCustomerName(orderAfter.customer_name ?? '')
          setCustomerEmail(orderAfter.customer_email ?? '')
          setCustomerPhone(orderAfter.customer_phone ?? '')
          setGuestBirthday(toGuestDateInputValue(orderAfter.guest_birthday))
          setDeliveryAddress(orderAfter.delivery_address ?? '')
          setDeliveryFeeOverride(orderAfter.delivery_fee_override ?? null)
        }
      }

      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['counterPendingOrders'] })

      // Mid-split tenders print only the guest slip (above). The standard
      // receipt path below runs for normal tenders and for the completing
      // tender of a split (the table's full fiscal receipt).
      if (variables.splitPrint && !fullyPaid) return

      try {
        const orderRes =
          orderAfter != null
            ? { success: true as const, data: orderAfter }
            : await apiClient.getOrder(variables.orderId)
        const settingsRes = await queryClient.fetchQuery({
          queryKey: ['settings', 'all'],
          queryFn: () => apiClient.getAllSettings(),
        })
        if (orderRes.success && orderRes.data && settingsRes.success && settingsRes.data) {
          const paidAt = new Date()
          const paymentMethod = variables.paymentData.payment_method
          const receiptSettings = parseReceiptSettings(
            settingsRes.data as Record<string, unknown>,
          )
          const fd = orderRes.data.fiscal_details
          /** Wait for localhost IMSFiscal before printing once — avoids provisional then duplicate slip. */
          const needsLocalFiscalRelay =
            orderRes.data.status === 'completed' &&
            String(fd?.status ?? '').toUpperCase() === 'PENDING_LOCAL' &&
            !!(fd?.local_payload && fd.local_payload.length > 0)

          let orderForReceipt = orderRes.data
          let receiptPrinted = false

          // Per-receipt loyalty claim + auto thank-you blocks — both
          // best-effort, fetched in parallel so the network hops overlap.
          // Failures are swallowed — a marketing hiccup must never
          // prevent the receipt from printing.
          // Deployments that hide the Marketing Suite skip the loyalty and
          // thank-you blocks entirely.
          const [loyaltyClaim, thankYouPromo, receiptCardPromoLabel] = await Promise.all([
            marketingOn
              ? buildReceiptLoyaltyBlock(orderRes.data.id, {
                  logoUrl: receiptSettings.logoUrl,
                  enabled:
                    (settingsRes.data as Record<string, unknown>).branded_qr_enabled !== false,
                })
              : null,
            marketingOn ? buildReceiptThankYouBlock(orderRes.data.id) : null,
            // Bank card promo label ("HBL Card Discount (20%)") for the
            // discount row — best-effort like the other two blocks.
            apiClient
              .getAppliedCardPromo(orderRes.data.id)
              .then((r) => (r.data?.applied ? r.data.label ?? null : null))
              .catch(() => null),
          ])

          const printFinalReceipt = (order: typeof orderForReceipt) => {
            const { printed } = printCustomerReceipt(order, receiptSettings, {
              cashierName: getCashierNameFromStorage(),
              paymentMethod,
              paidAt,
              orderTypeLabel: displayLabelForOrderType(order.order_type, orderTypesRaw),
              formatAmount: formatCurrency,
              billKind: 'final',
              loyaltyClaim,
              thankYouPromo,
              cardPromoLabel: receiptCardPromoLabel,
            })
            receiptPrinted = printed
          }

          if (!needsLocalFiscalRelay) {
            printFinalReceipt(orderForReceipt)
          } else {
            try {
              const localResp = await fetch(
                'http://localhost:8524/api/IMSFiscal/GetInvoiceNumberByModel',
                {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: fd.local_payload!,
                },
              )
              const localJson = await localResp.text()
              let invoiceNumber = ''
              let qrCode = ''
              let errorMsg = ''
              try {
                const parsed = JSON.parse(localJson) as Record<string, unknown>
                const code = String(parsed.Code ?? '')
                if (code === '100' || code === '') {
                  invoiceNumber = String(parsed.InvoiceNumber ?? '')
                  qrCode = String(
                    (parsed.QRCode as string | undefined) ??
                      (parsed.QrCode as string | undefined) ??
                      '',
                  )
                  if (!invoiceNumber || invoiceNumber.toLowerCase() === 'not available') {
                    invoiceNumber = ''
                    errorMsg =
                      String(parsed.Response ?? parsed.Errors ?? '') ||
                      'No invoice number returned'
                  }
                } else {
                  errorMsg = `Code ${code}: ${String(parsed.Response ?? parsed.Errors ?? 'Rejected')}`
                }
              } catch {
                errorMsg = 'Invalid JSON from IMSFiscal'
              }
              await apiClient.postLocalFiscalResult(variables.orderId, {
                invoice_number: invoiceNumber,
                qr_code: qrCode,
                raw_response: localJson,
                error: errorMsg,
              })

              if (invoiceNumber) {
                toastHelpers.success('FBR', `Invoice ${invoiceNumber} registered via local server`)
                let synced: Order | null = null
                for (let attempt = 0; attempt < 12; attempt++) {
                  const refreshed = await apiClient.getOrder(variables.orderId)
                  const o = refreshed.success ? refreshed.data : null
                  const f = o?.fiscal_details
                  if (
                    o &&
                    String(f?.status ?? '').toUpperCase() === 'SYNCED' &&
                    String(f?.irn ?? '').trim() !== ''
                  ) {
                    synced = o
                    break
                  }
                  await new Promise((r) => setTimeout(r, 100))
                }
                if (synced) orderForReceipt = synced
                else {
                  const fallback = await apiClient.getOrder(variables.orderId)
                  if (fallback.success && fallback.data) orderForReceipt = fallback.data
                }
                printFinalReceipt(orderForReceipt)
              } else {
                toastHelpers.error('FBR', errorMsg || 'Local fiscal server returned no invoice')
                printFinalReceipt(orderForReceipt)
              }
            } catch (localErr: unknown) {
              const msg = localErr instanceof Error ? localErr.message : ''
              const errMsg =
                msg.includes('Failed to fetch') || msg.includes('NetworkError')
                  ? 'IMSFiscal not running — ensure the local fiscal server is started on this machine'
                  : msg || 'Local fiscal relay failed'
              toastHelpers.error('FBR Local', errMsg)
              await apiClient
                .postLocalFiscalResult(variables.orderId, {
                  invoice_number: '',
                  qr_code: '',
                  raw_response: '',
                  error: errMsg,
                })
                .catch(() => {})
              printFinalReceipt(orderForReceipt)
            }
          }

          if (!receiptPrinted) {
            toastHelpers.error(
              'Receipt',
              'Popup blocked — allow popups for this site to print the receipt.',
            )
          }

          if (
            receiptSettings.praInvoiceEnabled &&
            orderForReceipt.status === 'completed'
          ) {
            setPraPromptContext({
              order: orderForReceipt,
              settings: receiptSettings,
              paymentMethod,
              paidAt,
            })
          }
        }
      } catch {
        /* receipt is optional */
      }
    },
  })

  /**
   * Dismiss the PRA tax invoice prompt without printing — the main receipt
   * already went to the printer, nothing else needs to happen.
   */
  const handlePraSkip = useCallback(() => {
    setPraPromptContext(null)
  }, [])

  /**
   * Print the PRA tax invoice slip, persist the print event, then dismiss.
   * Persistence failures don't block the printed slip — the receipt is
   * already in the customer's hand, so we log rather than surface an error.
   */
  const handlePraPrint = useCallback(async () => {
    if (!praPromptContext || praPrinting) return
    setPraPrinting(true)
    try {
      const { order, settings, paymentMethod, paidAt } = praPromptContext
      const { invoiceNumber, printed } = await printPraTaxInvoice(order, settings, {
        cashierName: getCashierNameFromStorage(),
        paymentMethod,
        paidAt,
        orderTypeLabel: displayLabelForOrderType(order.order_type, orderTypesRaw),
        formatAmount: formatCurrency,
      })
      // Only record the print event when the print window actually opened.
      // If the browser's popup blocker intercepted the window, the cashier
      // still sees the prompt close — but we do not lie to the audit trail.
      if (printed) {
        try {
          await apiClient.markPraInvoicePrinted(order.id, invoiceNumber || undefined)
          queryClient.invalidateQueries({ queryKey: ['orders'] })
          queryClient.invalidateQueries({ queryKey: ['order', order.id] })
        } catch {
          /* audit log is best-effort; print already succeeded */
        }
      }
    } finally {
      setPraPrinting(false)
      setPraPromptContext(null)
    }
  }, [praPromptContext, praPrinting, formatCurrency, queryClient])

  const checkoutIntentMutation = useMutation({
    mutationFn: ({
      orderId,
      intent,
    }: {
      orderId: string
      intent: 'cash' | 'card' | 'online'
    }) => apiClient.updateCheckoutIntent(orderId, { checkout_payment_method: intent }),
    onSuccess: (res, variables) => {
      if (res.success && res.data) {
        const order = res.data
        setSelectedOrder(order)
        queryClient.setQueryData(['order', variables.orderId, 'payment-panel'], order)
        // Keep the rail "On order" section in sync whenever checkout intent updates;
        // the returned order is always the ticket being rung out (variables.orderId).
        if (order.id === variables.orderId) {
          setExistingOrder(order)
        }
      }
      queryClient.invalidateQueries({ queryKey: ['counterPendingOrders'] })
    },
    onError: (err) => {
      // F-FE-N31: this very failure is what REVEALED the outage — the same two
      // conditions noteRequestFailure() keys off in api/client.ts, so by the
      // time we get here the offline banner is already up. A second, redder
      // message saying "check the connection" adds nothing the cashier does not
      // now know, and it lands on top of the rail during service. Stay quiet;
      // no money can move while the backend is unreachable anyway.
      if (
        err instanceof ApiClientError &&
        (err.isNetworkError ||
          (err.status === PROXY_UNREACHABLE_STATUS && err.code === PROXY_UNREACHABLE_CODE))
      ) {
        return
      }
      // Bank card promo is card-tender-conditional: the backend refused the
      // switch rather than silently dropping the discount. Quiet here — the
      // startIntentSwitch catch (which knows the attempted order + intent)
      // opens the "Remove promo & switch?" dialog for the cashier.
      if (err instanceof ApiClientError && err.code === 'card_promo_requires_card_tender') {
        return
      }
      // F-MM-N7: a tender switch that fails used to be completely silent — the
      // chip flipped, the server never re-priced, and the next tap paid the old
      // tender's total. Say so. The pay button stays live either way: the
      // backend arbitrates, and refusing to let the till take money over a
      // failed re-price would be a far worse trade.
      toastHelpers.error(
        'Payment type',
        "Couldn't switch payment type — check the connection. The total on screen may be for the previous type."
      )
    },
  })

  /**
   * The in-flight tender switch, so a pay tap can wait for the re-priced total
   * instead of sending the one currently on screen (F-MM-N7).
   *
   * Resolves to the fresh order, or null if the switch failed / timed out. It
   * never rejects — callers treat "no answer" as "carry on and let the backend
   * arbitrate", which is the whole point: this must never be able to block a sale.
   */
  const pendingIntentRef = useRef<{ p: Promise<Order | null>; settled: boolean } | null>(null)

  const startIntentSwitch = useCallback(
    (orderId: string, intent: 'cash' | 'card' | 'online') => {
      const entry: { p: Promise<Order | null>; settled: boolean } = {
        settled: false,
        p: Promise.resolve(null),
      }
      // F-FE-N31: don't ask a backend we know is unreachable. Offline this
      // fired on every tap that opened a ticket — including simply loading an
      // open order to add to it — and each one produced a red "check the
      // connection" toast over the rail. There is nothing to re-price offline:
      // no payment can be taken, so no tender can be chosen. "No answer" is
      // already this function's documented result, so returning it directly is
      // the same contract without the request or the noise.
      if (!online) {
        entry.settled = true
        pendingIntentRef.current = entry
        return entry.p
      }
      entry.p = checkoutIntentMutation
        .mutateAsync({ orderId, intent })
        .then((res) => (res?.success && res.data ? (res.data as Order) : null))
        .catch((err) => {
          // Card promo is card-tender-conditional: the backend blocked the
          // switch off 'card'. Surface the cashier's choice ("remove promo
          // and switch?") — this catch is the one place that still knows
          // which order and which target tender were attempted.
          if (err instanceof ApiClientError && err.code === 'card_promo_requires_card_tender') {
            setCardPromoTenderConflict({ orderId, intent })
          }
          return null
        })
        .then((order) => {
          entry.settled = true
          return order
        })
      pendingIntentRef.current = entry
      return entry.p
    },
    [checkoutIntentMutation, online]
  )

  /**
   * Wait for an in-flight tender switch, but never for long.
   *
   * The axios timeout is 30s (api/client.ts), so a hung request — captive
   * portal, packet loss, the kind of wifi restaurants actually have — would
   * park a naive `disabled={isPending}` button for half a minute. A till that
   * cannot take money is a worse failure than the stale amount this is
   * avoiding, and the backend already refuses a stale amount safely. So: cap
   * the wait, then proceed regardless.
   */
  const INTENT_SETTLE_CAP_MS = 1500
  const settleIntent = useCallback(async (): Promise<Order | null> => {
    const entry = pendingIntentRef.current
    // Nothing in flight. Deliberately returns null rather than the last
    // resolved order: once a switch has settled, React state has caught up and
    // `selectedOrder` is the better source — it also carries anything that
    // happened SINCE, such as a discount or promo applied after the switch.
    // Replaying a stale resolved order here would pay the pre-discount amount.
    if (!entry || entry.settled) return null
    let timer: ReturnType<typeof setTimeout> | undefined
    const capped = new Promise<null>((resolve) => {
      timer = setTimeout(() => resolve(null), INTENT_SETTLE_CAP_MS)
    })
    try {
      return await Promise.race([entry.p, capped])
    } finally {
      if (timer) clearTimeout(timer)
    }
  }, [])

  const discountMutation = useMutation({
    mutationFn: ({
      orderId,
      discount_amount,
      discount_percent,
    }: {
      orderId: string
      discount_amount?: number
      discount_percent?: number
    }) => apiClient.applyOrderDiscount(orderId, { discount_amount, discount_percent }),
    onSuccess: (res, vars) => {
      if (res.success && res.data) {
        setSelectedOrder(res.data)
        setExistingOrder((prev) => (prev?.id === res.data!.id ? res.data! : prev))
      }
      setDiscountValue('')
      queryClient.invalidateQueries({ queryKey: ['order', vars.orderId, 'payment-panel'] })
      queryClient.invalidateQueries({ queryKey: ['counterPendingOrders'] })
    },
  })

  // Per-order delivery fee override. For an existing order we PATCH and let the
  // server recompute totals; for an unplaced ticket we stash the override
  // locally so it flows into CreateOrder. Mirrors discountMutation's state sync.
  const deliveryFeeMutation = useMutation({
    mutationFn: ({ orderId, fee }: { orderId: string; fee: number | null }) =>
      apiClient.setOrderDeliveryFee(orderId, fee),
    onSuccess: (res, vars) => {
      if (res.success && res.data) {
        setSelectedOrder((prev) => (prev?.id === res.data!.id ? res.data! : prev))
        setExistingOrder((prev) => (prev?.id === res.data!.id ? res.data! : prev))
        setDeliveryFeeOverride(res.data.delivery_fee_override ?? null)
      } else if (!res.success) {
        toastHelpers.error('Delivery fee', res.message || 'Could not update the delivery fee.')
      }
      queryClient.invalidateQueries({ queryKey: ['order', vars.orderId, 'payment-panel'] })
      queryClient.invalidateQueries({ queryKey: ['counterPendingOrders'] })
    },
  })

  // Apply (number) or clear (null) the delivery fee. Routes to the server when
  // the order exists; otherwise updates the local override on the compose ticket.
  const onEditDeliveryFee = useCallback(
    (fee: number | null) => {
      const orderId = existingOrder?.id ?? continuingOrderId ?? selectedOrder?.id ?? null
      if (orderId) {
        deliveryFeeMutation.mutate({ orderId, fee })
      } else {
        setDeliveryFeeOverride(fee)
      }
    },
    [existingOrder?.id, continuingOrderId, selectedOrder?.id, deliveryFeeMutation]
  )

  // Per-line discount mutation. Mirrors discountMutation but scoped to
  // a single OrderItem. The server enforces the promo mutex (409
  // conflict_promo) and the line-subtotal cap; we surface those as
  // toasts so the cashier knows why the call failed without re-deriving
  // the constraint client-side.
  const itemDiscountMutation = useMutation({
    mutationFn: ({
      orderId,
      itemId,
      discount_amount,
      discount_percent,
      pin,
    }: {
      orderId: string
      itemId: string
      discount_amount?: number
      discount_percent?: number | null
      pin?: string
    }) =>
      apiClient.applyOrderItemDiscount(orderId, itemId, {
        discount_amount,
        discount_percent,
        pin,
      }),
    onSuccess: (res, vars) => {
      if (res.success && res.data) {
        setSelectedOrder(res.data)
        setExistingOrder((prev) => (prev?.id === res.data!.id ? res.data! : prev))
      } else if (!res.success) {
        const msg = res.error === 'conflict_promo'
          ? 'Remove the promo to apply a line discount.'
          : res.message || 'Could not apply line discount.'
        toastHelpers.error('Line discount', msg)
      }
      queryClient.invalidateQueries({ queryKey: ['order', vars.orderId, 'payment-panel'] })
      queryClient.invalidateQueries({ queryKey: ['counterPendingOrders'] })
    },
    onError: (err: Error) => {
      toastHelpers.error('Line discount', err.message || 'Could not apply line discount.')
    },
  })

  const categoryById = useMemo(() => {
    const m = new Map<string, Category>()
    ;(categories as Category[]).forEach((c) => m.set(c.id, c))
    return m
  }, [categories])

  /**
   * Product id → menu name, for rendering a held offline order (F-FE-N31).
   * The queue stores ids only, so names come from the live menu — which is also
   * what makes a price change during the outage impossible to resurrect.
   * Falls back to the id rather than a blank: an unrecognisable line the
   * cashier can still read out beats a row that looks empty.
   */
  const offlineProductName = useCallback(
    (productId: string) => products.find((p) => p.id === productId)?.name ?? productId,
    [products]
  )

  /**
   * The cart is adding to a ticket that already exists — server-side
   * (continuingOrderId) or held on this device (offlineTarget). Both mean the
   * same thing to the composer: the primary action appends, and the guest-detail
   * guards that only apply to creating an order do not apply.
   */
  const appendingToLoadedOrder = Boolean(continuingOrderId || offlineTarget)

  /**
   * Whether this terminal can fire the cart with no connection.
   *
   * An append is allowed even when the reserved-number pool has run dry: it
   * rides on a number that is already printed on the kitchen ticket, so it
   * consumes nothing. Blocking it would mean that in a long outage — exactly
   * when the pool empties — the tables already seated could no longer order,
   * which is the worst moment to stop serving. Every other refusal still
   * stands: without the desktop bridge or cached station routing there is no
   * way to put a ticket in front of the kitchen at all.
   */
  const offlineFireReady =
    offlineCapture.capability.ok ||
    (appendingToLoadedOrder && offlineCapture.capability.reason === 'no_reserved_numbers')

  // Total item count across all categories, for the "All" tile badge. Summed
  // from per-category product_count so it stays stable regardless of which
  // category is currently selected (products[] is category-scoped on filter).
  const totalCategoryItems = useMemo(
    () =>
      (categories as Category[]).reduce(
        (sum, c) => sum + (c.product_count ?? 0),
        0
      ),
    [categories]
  )

  const isSearching = searchTerm.trim().length > 0

  /**
   * Search deliberately IGNORES the selected category.
   *
   * A cashier typing into the search box is telling you what they want; if they
   * knew which tab it lived under they would have tapped it. Scoping the search
   * to the current tab means "brownie" returns nothing while Matcha is selected,
   * which reads as the product being missing.
   *
   * The category chip stays visually selected during a search and the view snaps
   * back to it when the box is cleared — search is a temporary overlay, not a
   * navigation change, so the cashier never loses their place mid-rush.
   *
   * SKU and barcode are matched too, so a scanner works in this box.
   */
  const filteredProducts = useMemo(() => {
    const q = searchTerm.trim().toLowerCase()
    const base =
      isSearching || selectedCategory === 'all'
        ? products
        : products.filter((p) => p.category_id === selectedCategory)
    if (!q) return base
    return base.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        (p.description?.toLowerCase().includes(q) ?? false) ||
        (p.sku?.toLowerCase().includes(q) ?? false) ||
        (p.barcode?.toLowerCase().includes(q) ?? false),
    )
  }, [products, selectedCategory, searchTerm, isSearching])

  const searchScopeNotice =
    isSearching && selectedCategory !== 'all'
      ? categories.find((c) => c.id === selectedCategory)?.name ?? null
      : null

  const cartSubtotal = cart.reduce((t, item) => t + item.product.price * item.quantity, 0)
  const cartTotals = computeCartTotals(cartSubtotal, 0, createCheckoutIntent, pricing, {
    deliveryFee: effectiveDeliveryFee,
  })
  // Total quantity of unsent draft items in the cart — feeds the Ticket tab
  // badge in narrow-mode so cashiers get visible confirmation when adding from
  // the Menu tab (the cart panel itself is hidden in that layout).
  const unsentCount = cart.reduce((n, item) => n + item.quantity, 0)

  // Hold the desktop update banner's restart while a checkout is open (F-FE-N47).
  // The cart itself is NOT a reason — useCounterDraft persists it and restores
  // it on boot. A half-entered tender does not survive, so that is.
  useBlockRestart('Finish the open checkout first', checkoutOpen)

  const addToCart = (product: Product) => {
    const existingItem = cart.find((item) => item.product.id === product.id)
    const gate = canAddToCart(product, existingItem?.quantity ?? 0, blockOversell)
    if (!gate.ok) {
      toastHelpers.warning('Cannot add', gate.reason)
      return
    }
    const wasExisting = Boolean(existingItem)
    if (existingItem) {
      setCart(
        cart.map((item) =>
          item.product.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
        )
      )
    } else {
      setCart([...cart, { product, quantity: 1 }])
    }
    if (tapFlashTimeoutRef.current != null) clearTimeout(tapFlashTimeoutRef.current)
    setLastTappedProductId(product.id)
    tapFlashTimeoutRef.current = setTimeout(() => {
      tapFlashTimeoutRef.current = null
      setLastTappedProductId((current) => (current === product.id ? null : current))
    }, 240)
    // Trigger the "+1" pop. Date.now() guarantees a new key on every tap (even
    // rapid-fire on the same product) so the keyframe restarts cleanly.
    setTapPop({ productId: product.id, nonce: Date.now() })

    if (cartFlashTimeoutRef.current != null) clearTimeout(cartFlashTimeoutRef.current)
    setLastCartFlashProductId(product.id)
    cartFlashTimeoutRef.current = setTimeout(() => {
      cartFlashTimeoutRef.current = null
      setLastCartFlashProductId((current) => (current === product.id ? null : current))
    }, 280)

    setCartLiveAnnouncement({
      text: wasExisting ? `Updated ${product.name} in cart` : `Added ${product.name} to cart`,
      id: Date.now(),
    })

    requestAnimationFrame(() => {
      cartRowRefs.current[product.id]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    })
  }

  const removeFromCart = (productId: string) => {
    const existingItem = cart.find((item) => item.product.id === productId)
    if (existingItem && existingItem.quantity > 1) {
      setCart(
        cart.map((item) =>
          item.product.id === productId ? { ...item, quantity: item.quantity - 1 } : item
        )
      )
    } else {
      setCart(cart.filter((item) => item.product.id !== productId))
    }
  }

  /**
   * Update the per-line `special_instructions` on a staged cart item. The
   * note rides through the existing submit pipeline (`createCounterOrder` and
   * `addItemsToOrder` both forward `special_instructions`) and is rendered by
   * the KOT printer (`kot.go` line 885) and the KDS payload (`kot.go` line
   * 273). Empty strings are stored as undefined so the JSON payload stays
   * tidy and KOT lines without a note skip the `>>` decoration.
   */
  const setItemNotes = useCallback((productId: string, note: string) => {
    const trimmed = note.trim()
    setCart((prev) =>
      prev.map((item) =>
        item.product.id === productId
          ? { ...item, special_instructions: trimmed === '' ? undefined : trimmed }
          : item
      )
    )
  }, [])

  /**
   * F-FE-N30: take the order on this device and fire its kitchen ticket
   * locally. Only reachable when offlineCapture.capability.ok — i.e. desktop
   * shell, a reserved number in hand, and station routing cached.
   *
   * Ordering matters. The order is queued for replay BEFORE the ticket prints,
   * so a crash between the two leaves a queued order with no ticket (recoverable
   * — the kitchen simply never saw it) rather than a printed ticket with no
   * queued order, which is food out the door with no record.
   */
  const handleOfflineSubmitCart = async () => {
    const userId = getDraftUserId()
    if (!userId) {
      toastHelpers.error('Offline order', 'Could not identify the signed-in user.')
      return
    }

    const result = await offlineCapture.captureOffline({
      // F-FE-N31: a held order has no server id, so its append is a local
      // mutation of the queue entry. It wins over targetOrderId — an order
      // cannot be both queued here and already live there.
      heldOpId: offlineTarget?.opId ?? null,
      // F-CTRL-N37: continuing an order that already exists on the server is an
      // APPEND, not a new order. Replay routes it to add-items, so the customer
      // is not billed for a duplicate order and the existing tab stays whole.
      targetOrderId: offlineTarget ? null : continuingOrderId,
      existingOrderNumber: offlineTarget?.orderNumber ?? existingOrder?.order_number,
      orderType,
      orderNote: orderNotes,
      userId,
      lines: cart.map((c) => ({
        productId: c.product.id,
        quantity: c.quantity,
        special_instructions: c.special_instructions,
        name: c.product.name,
        categoryId: c.product.category_id ?? '',
      })),
    })

    if (!result.ok) {
      if (result.reason === 'already_synced') {
        // F-FE-N31: the queue drained while the cashier was composing, so the
        // order is a real one now and the local copy is no longer the way in.
        // The cart is deliberately kept — those items were typed for a
        // customer, and clearing them would make the round vanish.
        const num = offlineTarget?.orderNumber ?? ''
        setOfflineTarget(null)
        setOfflineTrayNonce((n) => n + 1)
        toastHelpers.warning(
          `Order ${num} has synced`,
          'It is back in Open Orders. Your items are still in the cart — pick that order and add them.',
        )
        return
      }
      toastHelpers.error(
        'Offline order',
        'This terminal is not ready to take offline orders. Reconnect once to prepare it.',
      )
      return
    }

    // Keep the held order loaded so the same table's next round goes to the
    // same ticket, and re-read it so the "already sent" list shows what just
    // fired. A fresh capture does NOT stay loaded (see handleSubmitCart) —
    // there is no payment step offline to end the ticket, so it would sit
    // loaded until someone noticed, and the next customer's items would land
    // on the previous customer's order.
    if (result.held) {
      setOfflineTarget(result.held)
      setOfflineTrayNonce((n) => n + 1)
    }

    toastHelpers.success(
      result.held
        ? `Added to ${result.orderNumber}`
        : `Sale ${result.orderNumber} held`,
      'Held on this device. It will sync when you reconnect.',
    )
    clearOfflineCapturedCart()
  }

  const handleSubmitCart = () => {
    if (cart.length === 0) return
    // F-FE-N31: a held order lives only in this device's queue, so its next
    // round can only be appended locally — even if the connection came back a
    // moment ago. If it synced in the meantime, captureOffline says so and
    // hands the cashier to the real order rather than silently ringing a
    // duplicate through the online path.
    if (offlineTarget) {
      void handleOfflineSubmitCart()
      return
    }
    if (!online && offlineCapture.capability.ok) {
      void handleOfflineSubmitCart()
      return
    }
    submitCartMutation.mutate()
  }

  const selectPaymentOrder = (order: Order) => {
    setCheckoutOpen(true)
    setSelectedOrder(order)
    setSelectedOnlineSub(null)
    const intent = order.checkout_payment_method ?? 'cash'
    setPaymentCheckoutIntent(intent)
    startIntentSwitch(order.id, intent)
  }

  /**
   * Apply a fetched open sale to counter rail state (continuing order, guest
   * details, delivery). Does not open/close checkout — callers decide.
   */
  const applyActiveOrderToCounterState = useCallback(
    (od: Order) => {
      setExistingOrder(od)
      setContinuingOrderId(od.id)
      setExistingItemsExpanded(false)
      setCustomerName(od.customer_name ?? '')
      setCustomerEmail(od.customer_email ?? '')
      setCustomerPhone(od.customer_phone ?? '')
      setGuestBirthday(toGuestDateInputValue(od.guest_birthday))
      setDeliveryAddress(od.delivery_address ?? '')
      setDeliveryFeeOverride(od.delivery_fee_override ?? null)
    },
    []
  )

  /** Load full ticket context then open payment — fixes dine-in menu when using Orders to close. */
  const ordersStripCheckoutMutation = useMutation({
    mutationFn: async ({
      order,
      orderTypeAtClick,
    }: {
      order: Order
      orderTypeAtClick: string
    }) => {
      // F-CTRL-N37: offline, work from the order the strip already holds.
      //
      // This fetch is a freshness refresh, not a load — `order` came from the
      // cached pending-orders query and carries everything the rail needs. With
      // the backend unreachable it threw, so a cashier could not open a tab
      // that was already on screen, and adding to an order opened before the
      // outage was impossible.
      //
      // The staleness is real but bounded: totals may lag a change made on
      // another terminal during the outage. Appending is additive and the
      // server recomputes totals on replay, so the worst case is a briefly
      // out-of-date figure on screen — against not being able to serve at all.
      if (!online) {
        return { od: order, orderTypeAtClick }
      }
      const r = await apiClient.getOrder(order.id)
      if (!r.success || !r.data) {
        throw new Error(r.message || 'Could not load order')
      }
      return { od: r.data, orderTypeAtClick }
    },
    onSuccess: ({ od, orderTypeAtClick }) => {
      if (normalizeOrderTypeId(od.order_type) !== normalizeOrderTypeId(orderTypeAtClick)) {
        toastHelpers.error(
          'Orders',
          `Switch to ${displayLabelForOrderType(od.order_type, orderTypesRaw)} mode to work this ticket.`
        )
        return
      }
      applyActiveOrderToCounterState(od)
      setCart([])
      setOrderNotes('')
      clearDraft(draftUserId)
      selectPaymentOrder(od)
    },
    onError: (e: Error) => {
      toastHelpers.error('Orders', e.message || 'Could not load order')
    },
  })

  const handleGuestUpdated = useCallback((order: Order) => {
    setExistingOrder(order)
    setSelectedOrder((prev) => (prev?.id === order.id ? order : prev))
    setCustomerName(order.customer_name ?? '')
    setCustomerEmail(order.customer_email ?? '')
    setCustomerPhone(order.customer_phone ?? '')
    setGuestBirthday(toGuestDateInputValue(order.guest_birthday))
    setDeliveryAddress(order.delivery_address ?? '')
    setDeliveryFeeOverride(order.delivery_fee_override ?? null)
  }, [])

  /**
   * Open a past order from the Order history popover. A settled/cancelled one
   * opens read-only; a still-open one of the current order type is loaded onto
   * the rail so the cashier can add to it or settle it.
   */
  const handleSelectHistoryOrder = useCallback(
    async (o: Order) => {
      if (o.status === 'completed' || o.status === 'cancelled') {
        const full = await apiClient.getOrder(o.id)
        setHistoryReadOnlyOrder(full.success && full.data ? full.data : o)
        return
      }
      const r = await apiClient.getOrder(o.id)
      if (!r.success || !r.data) {
        toastHelpers.error('Orders', r.message || 'Could not load order')
        return
      }
      const od = r.data
      if (normalizeOrderTypeId(od.order_type) !== normalizeOrderTypeId(orderType)) {
        toastHelpers.error(
          'Orders',
          `Switch to ${displayLabelForOrderType(od.order_type, orderTypesRaw)} mode to work this sale.`
        )
        return
      }
      setCheckoutOpen(false)
      setSelectedOrder(null)
      applyActiveOrderToCounterState(od)
      // Intentionally no toast: the rail re-renders with the loaded sale's
      // order number, items, and status — the UI itself is the affirmation.
    },
    [orderType, applyActiveOrderToCounterState, orderTypesRaw]
  )

  const clearLoadedOrder = useCallback(() => {
    setCheckoutOpen(false)
    setSelectedOrder(null)
    setContinuingOrderId(null)
    setOfflineTarget(null)
    setExistingOrder(null)
    setCart([])
    setOrderNotes('')
    clearDraft(draftUserId)
    setCustomerName('')
    setCustomerEmail('')
    setCustomerPhone('')
    setGuestBirthday('')
    setCustomerSavedAddresses([])
    setDeliveryAddress('')
    setDeliveryFeeOverride(null)
    setExistingItemsExpanded(false)
    setPartialPaymentBanner(null)
  }, [])

  /**
   * Load a held order back into the rail so the next round can be added to it
   * (F-FE-N31).
   *
   * Tapping the loaded one again unloads it — the tray row is the only handle
   * on this state, so it has to be able to let go as well as take hold.
   */
  const handleSelectHeldOfflineOrder = useCallback(
    (o: OfflineOrder) => {
      // Unreconciled work belongs to a manager. Adding to something already
      // destined for a write-off would just make the write-off bigger.
      if (o.status !== 'pending') return
      if (offlineTarget?.opId === o.opId) {
        setOfflineTarget(null)
        return
      }
      // Same rule the Open Orders strip applies: the composer's order type
      // drives the slip header and the pricing preview, so working a ticket
      // from another mode would print the wrong thing.
      if (normalizeOrderTypeId(o.orderType) !== normalizeOrderTypeId(orderType)) {
        toastHelpers.error(
          'Held order',
          `Switch to ${displayLabelForOrderType(o.orderType, orderTypesRaw)} mode to add to this ticket.`
        )
        return
      }
      // Drop whatever else is loaded first: leaving a server-side order in
      // continuingOrderId while composing for a held one would let the fire
      // land on the wrong ticket entirely.
      clearLoadedOrder()
      setOfflineTarget(o)
    },
    [offlineTarget?.opId, orderType, orderTypesRaw, clearLoadedOrder]
  )

  /**
   * After a cart edit (items added / voided) the backend re-derives any
   * applied card promo from the new subtotal — and auto-removes it when the
   * bill drops below the promo's minimum. Refresh the promo state and, when
   * a promo the cashier could see disappears, say why with a toast instead
   * of leaving a silently vanished discount.
   */
  const refreshCardPromoAfterCartEdit = useCallback(
    async (orderId: string) => {
      queryClient.invalidateQueries({ queryKey: ['counter-eligible-card-promos', orderId] })
      const prev = queryClient.getQueryData<{ data?: { applied?: boolean; card_label?: string } }>([
        'counter-applied-card-promo',
        orderId,
      ])
      if (!prev?.data?.applied) {
        queryClient.invalidateQueries({ queryKey: ['counter-applied-card-promo', orderId] })
        return
      }
      try {
        const fresh = await apiClient.getAppliedCardPromo(orderId)
        queryClient.setQueryData(['counter-applied-card-promo', orderId], fresh)
        if (!fresh.data?.applied) {
          toastHelpers.info(
            'Card promo removed',
            `${prev.data.card_label ?? 'The bank card promo'} no longer applies — the bill dropped below its minimum.`
          )
        }
      } catch {
        queryClient.invalidateQueries({ queryKey: ['counter-applied-card-promo', orderId] })
      }
    },
    [queryClient]
  )

  /** Cashier confirmed: drop the card promo, then retry the tender switch. */
  const resolveCardPromoTenderConflict = useCallback(async () => {
    const conflict = cardPromoTenderConflict
    if (!conflict) return
    setCardPromoTenderConflict(null)
    try {
      await apiClient.removeCardPromoFromOrder(conflict.orderId)
    } catch {
      toastHelpers.error('Bank card promo', 'Could not remove the card promo — try again.')
      return
    }
    queryClient.invalidateQueries({ queryKey: ['counter-applied-card-promo', conflict.orderId] })
    queryClient.invalidateQueries({ queryKey: ['counter-eligible-card-promos', conflict.orderId] })
    queryClient.invalidateQueries({ queryKey: ['order', conflict.orderId, 'payment-panel'] })
    void startIntentSwitch(conflict.orderId, conflict.intent)
  }, [cardPromoTenderConflict, queryClient, startIntentSwitch])

  const handleVoidSuccess = useCallback(async (_itemId: string) => {
    const orderId = voidModal?.orderId
    setVoidModal(null)
    if (!orderId) return
    queryClient.invalidateQueries({ queryKey: ['orders'] })
    queryClient.invalidateQueries({ queryKey: ['counterPendingOrders'] })
    queryClient.invalidateQueries({ queryKey: ['order', orderId, 'payment-panel'] })
    void refreshCardPromoAfterCartEdit(orderId)
    try {
      const fresh = await apiClient.getOrder(orderId)
      if (fresh.success && fresh.data) {
        // F-CTRL-N30: voiding the LAST line makes the backend auto-cancel the
        // order (VoidItem's F-KDS-N2 branch). Before this check we kept the dead
        // order loaded as continuingOrderId, so the next punch appended a real
        // customer's items to a cancelled order and fired a KOT for it — the
        // food went out and the sale was never recorded (Chunkyard 20260729-021,
        // Rs 3,105). If the refetch comes back terminal, drop it and start clean.
        if (isTerminalOrderStatus(fresh.data.status)) {
          if (voidModal?.orderId === continuingOrderId || existingOrder?.id === orderId) {
            clearLoadedOrder()
          }
          toastHelpers.info(
            'Order closed',
            'Every item was voided, so this order was cancelled. Start a new order for the next customer.'
          )
          return
        }
        setExistingOrder((prev) => (prev?.id === orderId ? fresh.data! : prev))
        setSelectedOrder((prev) => (prev?.id === orderId ? fresh.data! : prev))
        queryClient.setQueryData(['order', orderId, 'payment-panel'], fresh.data)
      }
    } catch { /* best-effort refresh */ }
  }, [voidModal?.orderId, queryClient, continuingOrderId, existingOrder?.id, clearLoadedOrder])

  const handleCloseOrderFromHeader = useCallback(() => {
    if (cart.length > 0) {
      setDiscardCartConfirmOpen(true)
      return
    }
    clearLoadedOrder()
  }, [cart.length, clearLoadedOrder])

  // Extras shared by the payment panel and the split dialog so both compute
  // totals against the same delivery configuration.
  const payPricingExtras = useMemo(() => {
    if (!payOrder) return null
    const p = getOrderTypePricing(payOrder.order_type, orderTypesRaw)
    // Honor a per-order delivery fee override so the payment total matches the
    // server (which recomputes with the override) and the receipt.
    const payDeliveryFee =
      payOrder.order_type === 'delivery' && payOrder.delivery_fee_override != null
        ? payOrder.delivery_fee_override
        : p.deliveryFee
    return {
      deliveryFee: payDeliveryFee,
      itemDiscountsTotal: payOrder.item_discounts_total ?? 0,
    }
  }, [payOrder, orderTypesRaw])

  const paymentTotals = useMemo(() => {
    if (!payOrder || !payPricingExtras) return null
    return computeCartTotals(
      payOrder.subtotal,
      payOrder.discount_amount,
      paymentCheckoutIntent,
      pricing,
      payPricingExtras
    )
  }, [payOrder, payPricingExtras, paymentCheckoutIntent, pricing])

  const onPaymentIntent = (intent: 'cash' | 'card' | 'online') => {
    if (!selectedOrder) return
    if (intent !== 'cash') {
      setCashModalOpen(false)
      setCashReceived('')
    }
    if (intent !== 'online') {
      setSelectedOnlineSub(null)
    }
    setPaymentCheckoutIntent(intent)
    startIntentSwitch(selectedOrder.id, intent)
  }

  const remaining =
    payOrder &&
    (payOrder.total_amount -
      (payOrder.payments?.filter((p) => p.status === 'completed').reduce((s, p) => s + p.amount, 0) ?? 0))

  const payAmount = remaining ?? 0

  const hasPartialPayment =
    payOrder != null && (payOrder.payments ?? []).some((p) => p.status === 'completed')

  const handlePrintProvisionalBill = useCallback(async () => {
    if (!selectedOrder || !payOrder) return
    if (payAmount <= 0 || hasPartialPayment) return
    setProvisionalPrintBusy(true)
    try {
      const fresh = await apiClient.getOrder(selectedOrder.id)
      if (!fresh.success || !fresh.data) {
        toastHelpers.error('Print initial bill', fresh.message || 'Could not load order')
        return
      }
      const order = fresh.data
      if ((order.payments ?? []).some((p) => p.status === 'completed')) {
        toastHelpers.error(
          'Print initial bill',
          'Not available after a partial payment was recorded.',
        )
        return
      }
      const settingsRes = await queryClient.fetchQuery({
        queryKey: ['settings', 'all'],
        queryFn: () => apiClient.getAllSettings(),
      })
      if (!settingsRes.success || !settingsRes.data) {
        toastHelpers.error('Print initial bill', 'Could not load receipt settings')
        return
      }
      const cfg = parseReceiptSettings(settingsRes.data as Record<string, unknown>)
      const ot = getOrderTypePricing(order.order_type, orderTypesRaw)
      const { printed } = printUnpaidCustomerBill(
        order,
        cfg,
        {
          pricing,
          deliveryFee: ot.deliveryFee,
        },
        {
          cashierName: getCashierNameFromStorage(),
          serverName: getServerNameFromOrder(order),
          orderTypeLabel: displayLabelForOrderType(order.order_type, orderTypesRaw),
          formatAmount: formatCurrency,
          paidAt: new Date(),
          cardPromoLabel: order.id === activeRailOrderId ? activeCardPromoLabel : null,
        },
      )
      if (!printed) {
        toastHelpers.error('Print initial bill', 'Popup blocked — allow popups for this site')
      }
    } finally {
      setProvisionalPrintBusy(false)
    }
  }, [selectedOrder, payOrder, payAmount, hasPartialPayment, queryClient, orderTypesRaw, pricing, formatCurrency, activeRailOrderId, activeCardPromoLabel])

  const ticketLifecycle = useMemo(
    () =>
      getTicketLifecycle({
        order: existingOrder ?? payOrder,
        cartCount: cart.length,
        checkoutOpen,
        payableRemaining: payAmount,
      }),
    [existingOrder, payOrder, cart.length, checkoutOpen, payAmount]
  )

  const billableItems: OrderItem[] = useMemo(() => {
    const items = payOrder?.items ?? []
    return items.filter((i) => i.status !== 'voided')
  }, [payOrder?.items])

  // F-MM-N7: the amount is passed in rather than read from `payAmount` here.
  // These run AFTER an awaited tender switch, and the `payAmount` captured in
  // that closure is the pre-switch figure — the exact staleness we're closing.
  const runCardPayment = (amount: number) => {
    if (!selectedOrder || amount <= 0) return
    processPaymentMutation.mutate({
      orderId: selectedOrder.id,
      paymentData: {
        payment_method: 'card',
        amount,
      },
    })
  }

  // Active online sub-methods (migration 050). Empty when the feature is off,
  // in which case the plain online flow runs unchanged.
  const onlineSubMethods = pricing.online_submethods ?? []

  const runOnlinePayment = (amount: number, subMethod?: string) => {
    if (!selectedOrder || amount <= 0) return
    // Require a provider when sub-methods are configured (migration 050).
    if (onlineSubMethods.length > 0 && !subMethod) return
    processPaymentMutation.mutate({
      orderId: selectedOrder.id,
      paymentData: {
        payment_method: 'online',
        amount,
        ...(subMethod ? { payment_sub_method: subMethod } : {}),
      },
    })
  }

  const runCashPayment = () => {
    if (!selectedOrder || payAmount <= 0) return
    const received = parseFloat(cashReceived || '0')
    if (received < payAmount) return
    processPaymentMutation.mutate({
      orderId: selectedOrder.id,
      paymentData: { payment_method: 'cash', amount: payAmount },
    })
    setCashModalOpen(false)
    setCashReceived('')
  }

  /**
   * Remaining balance on a specific order object, rather than on whatever
   * `payOrder` happened to be at render time. Used after an awaited tender
   * switch, where the freshest order comes back from the mutation itself.
   */
  const remainingOn = (o: Order): number =>
    o.total_amount -
    ((o.payments ?? []).filter((p) => p.status === 'completed').reduce((s, p) => s + p.amount, 0) ?? 0)

  /** True while a pay tap is waiting on an in-flight tender switch (F-MM-N7). */
  const [payArming, setPayArming] = useState(false)

  /**
   * Primary pay action. Waits — briefly, and never indefinitely — for an
   * in-flight tender switch so the amount sent matches the tender being paid.
   *
   * Deliberately NOT `disabled={checkoutIntentMutation.isPending}`: with a 30s
   * axios timeout, a hung request would park the pay button for half a minute.
   * A till that cannot take money is a worse failure than the stale amount this
   * avoids, and the backend refuses a stale amount safely (F-MM-N7
   * order_reprice_conflict). So the cap expires and we proceed regardless.
   */
  const onPrimaryPay = async () => {
    if (payArming || processPaymentMutation.isPending) return
    const order = selectedOrder
    if (!order) return

    setPayArming(true)
    let fresh: Order | null = null
    try {
      fresh = await settleIntent()
    } finally {
      setPayArming(false)
    }

    // The operator moved to a different ticket while we waited — abandon rather
    // than pay the wrong order.
    if (!selectedOrderRef.current || selectedOrderRef.current.id !== order.id) return

    // Freshest known copy of this ticket, in order of authority: the order the
    // tender switch just returned, then the payment-panel query, then local
    // state. The panel copy outranks `selectedOrder` because it is what the
    // Charge button's figure is drawn from and it is refreshed on paths that
    // never touch `selectedOrder` — sending a number the operator was not shown
    // is how a stale copy turns into a rejected payment (F-MM-N10).
    const panel = payOrderRef.current?.id === order.id ? payOrderRef.current : null
    const local = selectedOrderRef.current?.id === order.id ? selectedOrderRef.current : null
    const effective =
      (fresh && fresh.id === order.id ? fresh : null) ?? panel ?? local ?? order
    const amount = remainingOn(effective)
    if (amount <= 0) return

    const intent = paymentCheckoutIntentRef.current
    if (intent === 'cash') {
      // Start the tender pad empty (amount received = 0) so the clerk enters
      // what the customer actually handed over, rather than backspacing a
      // pre-filled "exact" amount.
      setCashModalOpen(true)
      setCashReceived('')
    } else if (intent === 'card') {
      runCardPayment(amount)
    } else if (onlineSubMethods.length > 0) {
      runOnlinePayment(amount, selectedOnlineSub ?? undefined)
    } else {
      runOnlinePayment(amount)
    }
  }

  /** 100% comps / Rs 0 remaining: close via ProcessPayment self-heal (amount 0). */
  const runCompleteZeroBalance = useCallback(() => {
    if (!selectedOrder || payAmount > 0) return
    if (selectedOrder.status === 'completed' || selectedOrder.status === 'cancelled') return
    processPaymentMutation.mutate({
      orderId: selectedOrder.id,
      paymentData: {
        payment_method: paymentCheckoutIntent,
        amount: 0,
      },
    })
  }, [selectedOrder, payAmount, paymentCheckoutIntent, processPaymentMutation])

  // ----- Split bill (F-FE-N19) -----

  const splitState = payOrder?.split_state ?? null
  const splitPaidCount = (splitState?.guests ?? []).filter((g) => g.paid_payment_id).length
  // A pre-existing (non-split) partial payment would leave one tender
  // unlabeled and the shares ambiguous — cleanest to require a fresh check.
  const splitDisabledReason =
    !splitState && hasPartialPayment
      ? 'Unavailable after a partial payment — void payments to split'
      : billableItems.length === 0 && !splitState
        ? 'Nothing to split yet'
        : undefined

  /** Propagate the refreshed Order returned by the split endpoints. */
  const handleSplitOrderUpdated = useCallback(
    (order: Order) => {
      setSelectedOrder(order)
      setExistingOrder((prev) => (prev?.id === order.id ? order : prev))
      queryClient.setQueryData(['order', order.id, 'payment-panel'], order)
    },
    [queryClient]
  )

  const handlePaySplitGuest = useCallback(
    (args: PaySplitGuestArgs) => {
      if (!selectedOrder || !payOrder?.split_state) return
      // The rate the whole order is billed at: mixed splits use the standard
      // cash rate (never under-collect), card-only keeps the reduced rate.
      const rateIntent: 'cash' | 'card' =
        payOrder.split_state.tender_scope === 'card_only' ? 'card' : 'cash'
      processPaymentMutation.mutate({
        orderId: selectedOrder.id,
        paymentData: {
          payment_method: args.tender,
          amount: args.amount,
          ...(args.subMethod ? { payment_sub_method: args.subMethod } : {}),
          rate_intent: rateIntent,
          split_guest_no: args.guestNo,
        },
        splitPrint: {
          share: args.share,
          guestNo: args.guestNo,
          guestCount: payOrder.split_state.guest_count,
        },
      })
    },
    [selectedOrder, payOrder, processPaymentMutation]
  )

  const handleReprintSplitGuest = useCallback(
    (share: GuestShare) => {
      if (!payOrder?.split_state) return
      void printSplitGuestSlip(
        payOrder.id,
        payOrder,
        share,
        share.guestNo,
        payOrder.split_state.guest_count,
        share.tender ?? 'cash'
      )
    },
    [payOrder, printSplitGuestSlip]
  )

  const clampCheckoutRail = useCallback((w: number, containerWidth: number) => {
    const max = Math.max(
      COUNTER_RAIL_MIN,
      // The menu pane must keep room for ≥2 product tiles (2×190px minmax +
      // gap + pane padding + drag separator ≈ 444px) — otherwise tablets
      // (768–1024) render a single sparse product column beside a half-screen
      // rail. The old floor of MIN+160 forced exactly that at 820px.
      Math.min(820, Math.floor(containerWidth * 0.56), containerWidth - 444)
    )
    return Math.min(max, Math.max(COUNTER_RAIL_MIN, Math.round(w)))
  }, [])

  const beginCheckoutRailDrag = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!isSplitLayout) return
      if (e.pointerType === 'mouse' && e.button !== 0) return
      e.preventDefault()
      const root = counterSplitRef.current
      if (!root) return
      const target = e.currentTarget
      try {
        target.setPointerCapture(e.pointerId)
      } catch {
        /* ignore */
      }
      const cw = root.getBoundingClientRect().width
      // Same bound as clampCheckoutRail: never let the rail eat the menu's
      // two-column minimum (~444px) on narrow split layouts.
      const maxW = Math.max(
        COUNTER_RAIL_MIN,
        Math.min(820, Math.floor(cw * 0.56), cw - 444)
      )
      railDragRef.current = { startX: e.clientX, startW: checkoutRailPx, maxW }
      let lastShift = e.shiftKey
      const onMove = (ev: PointerEvent) => {
        const d = railDragRef.current
        if (!d) return
        lastShift = ev.shiftKey
        const delta = d.startX - ev.clientX
        const nw = d.startW + delta
        setCheckoutRailPx(Math.min(d.maxW, Math.max(COUNTER_RAIL_MIN, Math.round(nw))))
      }
      const SNAP_POINTS = [400, 480, 560, 640]
      const onUp = () => {
        const useSnap = !lastShift
        railDragRef.current = null
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
        document.body.style.removeProperty('-webkit-user-select')
        try {
          target.releasePointerCapture(e.pointerId)
        } catch {
          /* ignore */
        }
        target.removeEventListener('pointermove', onMove)
        target.removeEventListener('pointerup', onUp)
        target.removeEventListener('pointercancel', onUp)
        setCheckoutRailPx((w) => {
          const cw2 = counterSplitRef.current?.getBoundingClientRect().width ?? 1200
          let next = clampCheckoutRail(w, cw2)
          if (useSnap) {
            const snap = SNAP_POINTS.reduce((acc, p) =>
              Math.abs(p - next) < Math.abs(acc - next) ? p : acc
            , next)
            if (Math.abs(snap - next) <= 22) next = clampCheckoutRail(snap, cw2)
          }
          try {
            localStorage.setItem(COUNTER_CHECKOUT_RAIL_KEY, String(next))
          } catch {
            /* ignore */
          }
          return next
        })
      }
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
      document.body.style.setProperty('-webkit-user-select', 'none')
      target.addEventListener('pointermove', onMove)
      target.addEventListener('pointerup', onUp)
      target.addEventListener('pointercancel', onUp)
    },
    [checkoutRailPx, clampCheckoutRail, isSplitLayout]
  )

  const resetCheckoutRailWidth = useCallback(() => {
    setCheckoutRailPx(COUNTER_RAIL_DEFAULT)
    try {
      localStorage.setItem(COUNTER_CHECKOUT_RAIL_KEY, String(COUNTER_RAIL_DEFAULT))
    } catch {
      /* ignore */
    }
  }, [])

  useEffect(() => {
    const onResize = () => {
      setCheckoutRailPx((w) => {
        const cw = counterSplitRef.current?.getBoundingClientRect().width ?? 1200
        return clampCheckoutRail(w, cw)
      })
    }
    // Clamp on mount too: a width persisted from a wide session could otherwise
    // crush the menu to ~0 when the split first appears on a narrow (≥768px) screen.
    onResize()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [clampCheckoutRail])

  useEffect(() => {
    try {
      localStorage.removeItem(LEGACY_CATEGORY_PANE_KEY)
    } catch {
      /* ignore */
    }
  }, [])

  // Category bar scroll/overflow tracking lives in MenuCategoryBar (layout="row").

  useEffect(() => {
    if (checkoutOpen && !isSplitLayout) setMobileWorkspaceTab('ticket')
  }, [checkoutOpen, isSplitLayout])

  useEffect(() => {
    if (!checkoutOpen) return
    const t = window.setTimeout(() => {
      const firstTender = document.querySelector<HTMLButtonElement>(
        '[aria-labelledby="tender-input-heading"] button[aria-pressed]'
      )
      firstTender?.focus()
    }, 40)
    return () => window.clearTimeout(t)
  }, [checkoutOpen])

  const handleCounterEscape = useCallback(() => {
    if (historyReadOnlyOrder) {
      setHistoryReadOnlyOrder(null)
      return
    }
    if (checkoutCelebration != null) {
      dismissCheckoutCelebration()
      return
    }
    if (cashModalOpen) {
      setCashModalOpen(false)
      setCashReceived('')
      return
    }
    if (checkoutOpen) {
      setCheckoutOpen(false)
      setSelectedOrder(null)
    }
  }, [historyReadOnlyOrder, checkoutCelebration, dismissCheckoutCelebration, cashModalOpen, checkoutOpen])

  useCounterHotkeys({
    onSend: () => {
      if (cart.length === 0) return
      submitCartMutation.mutate()
    },
    onPay: () => {
      if (existingOrder && continuingOrderId && !checkoutOpen && orderPayableRemaining(existingOrder) > 0) {
        selectPaymentOrder(existingOrder)
      }
    },
    onFocusDiscount: () => {
      if (!checkoutOpen) return
      const findInput = () =>
        document.querySelector<HTMLInputElement>(
          'input[placeholder^="e.g. 10"], input[placeholder="0.00"]'
        )
      const el = findInput()
      if (el) {
        el.focus()
        return
      }
      // Panel collapsed — expand the Discount accordion, then focus on next paint.
      const toggle = document.querySelector<HTMLButtonElement>(
        'button[aria-controls="counter-discount-panel"]'
      )
      toggle?.click()
      requestAnimationFrame(() => findInput()?.focus())
    },
    onEscape: handleCounterEscape,
    onFocusSearch: () => searchInputRef.current?.focus(),
    onFocusCart: () => {
      railScrollRef.current?.scrollTo({ top: railScrollRef.current.scrollHeight, behavior: 'smooth' })
    },
  })

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-1 flex-row bg-background">
      <PraInvoicePromptModal
        open={praPromptContext != null}
        onSkip={handlePraSkip}
        onPrint={handlePraPrint}
        busy={praPrinting}
      />

      {/* blockedReason is set only when a sale was actually blocked; opening
          this proactively from the banner shows no "your sale failed" alert. */}
      <OpenDayDialog
        open={startDayBlocker.open}
        onOpenChange={(open) => setStartDayBlocker({ open, message: startDayBlocker.message })}
        blockedReason={startDayBlocker.message}
        // F-CTRL-N32: counter tier confirms the standing float rather than
        // typing one. Reuses userCanRunDayClose (admin|manager) as the tier
        // test; the backend enforces the same rule independently, so this is
        // affordance only, not the control.
        confirmOnly={!userCanRunDayClose}
      />

      <PreviousDayBlockerModal
        open={previousDayBlocker.open}
        onOpenChange={(open) => setPreviousDayBlocker({ open, message: previousDayBlocker.message })}
        message={previousDayBlocker.message}
        canRunDayClose={userCanRunDayClose}
      />

      <ManagerHandoffModal open={handoffOpen} onClose={() => setHandoffOpen(false)} />

      {voidModal && (
        <PinEntryModal
          orderId={voidModal.orderId}
          itemId={voidModal.itemId}
          itemName={voidModal.itemName}
          quantity={voidModal.qty}
          unitPrice={voidModal.unitPrice}
          onSuccess={handleVoidSuccess}
          onClose={() => setVoidModal(null)}
        />
      )}

      <Dialog open={discardCartConfirmOpen} onOpenChange={setDiscardCartConfirmOpen}>
        <DialogContent className="max-w-md sm:rounded-lg">
          <DialogHeader>
            <DialogTitle>Discard staged items?</DialogTitle>
            <DialogDescription>
              Closing this order will discard the {cart.length} item{cart.length === 1 ? '' : 's'} you have added to the cart.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setDiscardCartConfirmOpen(false)}
            >
              Keep editing
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                setDiscardCartConfirmOpen(false)
                clearLoadedOrder()
              }}
            >
              Discard items
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={cardPromoTenderConflict != null}
        onOpenChange={(open) => !open && setCardPromoTenderConflict(null)}
      >
        <DialogContent className="max-w-md sm:rounded-lg">
          <DialogHeader>
            <DialogTitle>Remove the bank card promo?</DialogTitle>
            <DialogDescription>
              This order has a bank card discount that only applies when the bill is paid by card.
              Switching to {cardPromoTenderConflict?.intent === 'online' ? 'Online' : 'Cash'} will
              remove the discount and the total will go back up.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setCardPromoTenderConflict(null)}
            >
              Keep card promo
            </Button>
            <Button type="button" variant="destructive" onClick={resolveCardPromoTenderConflict}>
              Remove promo & switch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {checkoutCelebration != null && (
        <CounterCheckoutSuccessOverlay
          mode={checkoutCelebration}
          onDismiss={dismissCheckoutCelebration}
        />
      )}

      <Dialog open={historyReadOnlyOrder != null} onOpenChange={(open) => !open && setHistoryReadOnlyOrder(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {historyReadOnlyOrder ? `Order #${historyReadOnlyOrder.order_number}` : 'Order'}
            </DialogTitle>
            <DialogDescription className="space-y-1 text-left">
              <span className="block capitalize text-muted-foreground">
                {historyReadOnlyOrder?.status?.replace('_', ' ')} ·{' '}
                {historyReadOnlyOrder && formatCurrency(historyReadOnlyOrder.total_amount)}
              </span>
              {historyReadOnlyOrder?.created_at && (
                <span className="block text-xs text-muted-foreground">
                  {format(new Date(historyReadOnlyOrder.created_at), "MMM d, yyyy 'at' h:mm a")}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This ticket is closed. Use order history above to find open checks, or open{' '}
            <Link
              to="/admin/reports"
              className="font-medium text-primary underline-offset-2 hover:underline"
            >
              Reports
            </Link>{' '}
            for deeper analysis.
          </p>
          {historyReadOnlyOrder?.status === 'completed' ? (
            <div className="mt-2">
              <Button
                type="button"
                variant="outline"
                className="w-full border-[rgb(255,241,178)] bg-[rgb(255,241,178)] hover:bg-[rgb(255,241,178)]"
                disabled={historyGuestPrintBusy || praPrinting}
                onClick={async () => {
                  if (!historyReadOnlyOrder) return
                  setHistoryGuestPrintBusy(true)
                  try {
                    const r = await printGuestReceiptForOrderId(
                      apiClient,
                      queryClient,
                      historyReadOnlyOrder.id,
                      {
                        formatAmount: formatCurrency,
                        getCashierName: getCashierNameFromStorage,
                        isDuplicateCopy: true,
                      },
                    )
                    if (!r.ok) {
                      toastHelpers.error('Guest receipt', r.reason)
                    } else {
                      toastHelpers.success('Guest receipt', 'Sent to print dialog.')
                    }
                  } finally {
                    setHistoryGuestPrintBusy(false)
                  }
                }}
              >
                {historyGuestPrintBusy ? 'Printing…' : 'Print guest receipt'}
              </Button>
            </div>
          ) : null}
          {historyReadOnlyOrder?.pra_invoice_printed ? (
            <div className="mt-2 flex items-center justify-between gap-3 rounded-md border border-border bg-muted/30 p-3">
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium">PRA tax invoice printed</p>
                {historyReadOnlyOrder.pra_invoice_number ? (
                  <p className="text-[11px] text-muted-foreground truncate">
                    Invoice #{historyReadOnlyOrder.pra_invoice_number}
                  </p>
                ) : null}
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={praPrinting}
                onClick={async () => {
                  if (!historyReadOnlyOrder) return
                  setPraPrinting(true)
                  try {
                    const settingsRes = await queryClient.fetchQuery({
                      queryKey: ['settings', 'all'],
                      queryFn: () => apiClient.getAllSettings(),
                    })
                    if (!settingsRes.success || !settingsRes.data) return
                    const cfg = parseReceiptSettings(
                      settingsRes.data as Record<string, unknown>,
                    )
                    const paidAt = historyReadOnlyOrder.completed_at
                      ? new Date(historyReadOnlyOrder.completed_at)
                      : new Date()
                    const method = checkoutStoredIntentToReceiptPaymentMethod(
                      historyReadOnlyOrder.checkout_payment_method,
                    )
                    const { invoiceNumber, printed } = await printPraTaxInvoice(
                      historyReadOnlyOrder,
                      cfg,
                      {
                        cashierName: getCashierNameFromStorage(),
                        paymentMethod: method,
                        paidAt,
                        orderTypeLabel: displayLabelForOrderType(
                          historyReadOnlyOrder.order_type,
                          orderTypesRaw,
                        ),
                        formatAmount: formatCurrency,
                      },
                    )
                    if (printed) {
                      try {
                        await apiClient.markPraInvoicePrinted(
                          historyReadOnlyOrder.id,
                          invoiceNumber || undefined,
                        )
                        queryClient.invalidateQueries({ queryKey: ['orders'] })
                        queryClient.invalidateQueries({
                          queryKey: ['order', historyReadOnlyOrder.id],
                        })
                      } catch {
                        /* audit log is best-effort */
                      }
                    }
                  } finally {
                    setPraPrinting(false)
                  }
                }}
              >
                {praPrinting ? 'Printing…' : 'Reprint PRA'}
              </Button>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col self-stretch">
        <div
          ref={counterSplitRef}
          className="flex min-h-0 min-w-0 flex-1 flex-col self-stretch md:flex-row"
        >
        <div
          role="tablist"
          aria-label="Counter workspace"
          className="flex shrink-0 gap-2 border-b border-border bg-muted/40 px-3 py-2 md:hidden"
        >
          <Button
            type="button"
            role="tab"
            aria-selected={mobileWorkspaceTab === 'menu'}
            variant={mobileWorkspaceTab === 'menu' ? 'default' : 'outline'}
            size="sm"
            className="min-h-11 flex-1 touch-manipulation"
            onClick={() => setMobileWorkspaceTab('menu')}
          >
            Catalog
          </Button>
          <Button
            type="button"
            role="tab"
            aria-selected={mobileWorkspaceTab === 'ticket'}
            variant={mobileWorkspaceTab === 'ticket' ? 'default' : 'outline'}
            size="sm"
            className="relative min-h-11 flex-1 touch-manipulation"
            onClick={() => setMobileWorkspaceTab('ticket')}
          >
            Ticket
            {unsentCount > 0 && (
              <Badge
                key={unsentCount}
                className="counter-tab-badge-pop ml-2 border-transparent bg-orange-500 px-1.5 py-0 text-xs font-semibold tabular-nums text-white hover:bg-orange-500"
                aria-label={`${unsentCount} item${unsentCount === 1 ? '' : 's'} in draft`}
              >
                {unsentCount}
              </Badge>
            )}
          </Button>
        </div>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col md:min-h-0 md:flex-row">
        <main
          aria-label="Counter workspace"
          className={cn(
            'flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden border-r border-border bg-background',
            mobileWorkspaceTab === 'ticket' && 'hidden md:flex'
          )}
        >
        <StartTheDayBanner
          currentDay={currentDay}
          isLoading={dayOpsCurrentPending}
          onStartClick={() => setStartDayBlocker({ open: true })}
        />
        <DailyClosingBanner
          currentDay={currentDay}
          closeoutHour={closeoutHour}
          userRole={userRole}
          onCloseClick={() => navigate({ to: '/admin/day-close' })}
          onGetManagerClick={() => setHandoffOpen(true)}
        />
        <div className="shrink-0 border-b border-border bg-card/95 px-6 pt-4 pb-3 backdrop-blur-sm">
          <div className="mb-2">
            <h1 className="text-3xl font-bold tracking-tight">Point of Sale (POS)</h1>
            <p className="mt-1 text-muted-foreground">
              Choose order type, then add items from the menu
            </p>
          </div>

          <div className="mb-3">
            {enabledOrderTypeIds.size === 0 ? (
              <div className="rounded-2xl border border-dashed border-border bg-muted/20 p-4 text-sm text-muted-foreground">
                No order types are currently enabled. Ask an admin to enable at least one
                order type under Settings &rarr; Order Types.
              </div>
            ) : (
              <CounterOrderTypeToggle
                value={orderType}
                enabledIds={enabledOrderTypeIds}
                options={counterOrderToggleOptions}
                onChange={(next) => {
                  setOrderType(next)
                  setCheckoutOpen(false)
                  setSelectedOrder(null)
                  // Guest fields deliberately survive the switch (F-FE-N29) —
                  // e.g. customer picked first, then the cashier flips to
                  // delivery and the saved address auto-fills. Address + fee
                  // are per-type, so those still reset.
                  setDeliveryAddress('')
                  setDeliveryFeeOverride(null)
                }}
              />
            )}
          </div>

          {pendingOrdersLoading && ordersToCloseStrip.length === 0 && (
            <div
              style={railSurfaceStyle}
              className={cn(
                'flex items-center gap-2.5 rounded-lg px-2.5 py-1.5',
                !railAccent && 'bg-amber-500/[0.06]'
              )}
            >
              <div
                style={railAccent ? { color: railAccent } : undefined}
                className={cn(
                  'flex shrink-0 items-center gap-1.5 text-[10.7px] font-semibold uppercase tracking-wide',
                  !railAccent && 'text-amber-700 dark:text-amber-400'
                )}
              >
                <span
                  aria-hidden
                  className={cn('h-[6.3px] w-[6.3px] rounded-full', !railAccent && 'bg-amber-500')}
                  style={railAccent ? { background: railAccent } : undefined}
                />
                <span className="max-w-[70px] leading-tight">Open orders</span>
              </div>
              <div className="flex min-w-0 flex-1 gap-2 overflow-x-auto py-0.5">
                <Skeleton className="h-11 w-24 shrink-0 rounded-md" />
                <Skeleton className="h-11 w-24 shrink-0 rounded-md" />
                <Skeleton className="h-11 w-24 shrink-0 rounded-md" />
              </div>
            </div>
          )}
          {ordersToCloseStrip.length > 0 && (
            <div
              style={railSurfaceStyle}
              className={cn(
                'flex items-center gap-2.5 rounded-lg px-2.5 py-1.5',
                !railAccent && 'bg-amber-500/[0.06]'
              )}
            >
              <div
                style={railAccent ? { color: railAccent } : undefined}
                className={cn(
                  'flex shrink-0 items-center gap-1.5 text-[10.7px] font-semibold uppercase tracking-wide',
                  !railAccent && 'text-amber-700 dark:text-amber-400'
                )}
              >
                <span
                  aria-hidden
                  className={cn('h-[6.3px] w-[6.3px] rounded-full', !railAccent && 'bg-amber-500')}
                  style={railAccent ? { background: railAccent } : undefined}
                />
                <span className="max-w-[70px] leading-tight">Open orders</span>
              </div>
              <div className="flex min-w-0 flex-1 gap-2 overflow-x-auto py-0.5">
                {ordersToCloseStrip.map((order) => {
                  const isActive = activeRailOrderId === order.id
                  // The order's order-type color (yellow/orange for takeaway, green for
                  // dine-in, purple for delivery, etc.) carries the same identity it has
                  // everywhere else: `${hex}22` (~13% alpha) washes the hover, and the
                  // selected tile keeps that exact wash plus a solid-tint border/bar.
                  const tint = colorForOrderTypeId(orderTypesRaw, order.order_type)
                  const tileStyle = tint
                    ? ({
                        '--tile-hover': `${tint}22`,
                        '--tile-accent': tint,
                        ...(isActive
                          ? {
                              background: `${tint}22`,
                              borderColor: tint,
                              '--tw-ring-color': `${tint}66`,
                            }
                          : {}),
                      } as React.CSSProperties)
                    : undefined
                  return (
                    <Button
                      key={order.id}
                      type="button"
                      size="sm"
                      variant="outline"
                      style={tileStyle}
                      className={cn(
                        'relative shrink-0 h-auto min-h-11 flex-col items-stretch py-2 px-3 text-foreground hover:text-foreground',
                        // Soft order-type tint on hover; muted fallback if no color configured.
                        !isActive && (tint ? 'hover:bg-[var(--tile-hover)]' : 'hover:bg-muted/40'),
                        isActive && tint &&
                          'ring-1 before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-1 before:rounded-r before:bg-[var(--tile-accent)]',
                        isActive && !tint &&
                          'border-primary bg-primary/10 ring-1 ring-primary/40 hover:bg-primary/15 before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-1 before:rounded-r before:bg-primary'
                      )}
                      disabled={
                        checkoutIntentMutation.isPending || ordersStripCheckoutMutation.isPending
                      }
                      onClick={() =>
                        ordersStripCheckoutMutation.mutate({ order, orderTypeAtClick: orderType })
                      }
                    >
                      <span className="font-semibold">#{order.order_number}</span>
                      <span className="text-xs opacity-80 tabular-nums">{formatCurrency(order.total_amount)}</span>
                    </Button>
                  )
                })}
              </div>
            </div>
          )}

        </div>

        <div className="flex min-h-0 flex-1 flex-col">
          {/* Search + categories — one open section above the product grid
              (the header's bottom border draws the line after the rail). */}
          <div className="shrink-0 px-3 pt-3 sm:px-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                ref={searchInputRef}
                placeholder="Search or scan barcode / SKU"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter') return
                  e.preventDefault()
                  // A keyboard-wedge scanner types the code and presses Enter,
                  // so Enter IS the scan. Exact barcode/SKU wins; failing that,
                  // a search that has narrowed to one product rings that one —
                  // anything else leaves the list up rather than guessing.
                  const hit =
                    resolveScanMatch(products, searchTerm) ??
                    (filteredProducts.length === 1 ? filteredProducts[0] : null)
                  if (!hit) return
                  addToCart(hit)
                  setSearchTerm('')
                  searchInputRef.current?.focus()
                }}
                className="pl-10 h-11"
              />
            </div>
          </div>
          <MenuCategoryBar
            layout="row"
            categories={categories as Category[]}
            selectedCategory={selectedCategory}
            onSelect={setSelectedCategory}
            totalCount={totalCategoryItems}
          />

          {/* Items pane (bottom) — the product grid, filtered by selection. */}
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-3 sm:p-4">
          {searchScopeNotice && (
            <div className="mb-3 flex items-center justify-between gap-2 rounded-md border border-dashed bg-muted/30 px-3 py-2 text-xs">
              <span className="text-muted-foreground">
                Searching <strong className="text-foreground">all products</strong> — clear the search
                to return to {searchScopeNotice}.
              </span>
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="shrink-0 font-medium underline underline-offset-2"
              >
                Clear
              </button>
            </div>
          )}
          {filteredProducts.length === 0 && searchTerm.trim().length > 0 ? (
            <div className="flex h-full min-h-[12rem] flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border bg-muted/20 p-8 text-center">
              <p className="text-sm font-medium">No products match &ldquo;{searchTerm}&rdquo;.</p>
              <p className="text-xs text-muted-foreground">
                Try a different keyword or clear the search box.
              </p>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="grid gap-2 [grid-template-columns:repeat(auto-fill,minmax(140px,1fr))] sm:[grid-template-columns:repeat(auto-fill,minmax(190px,1fr))]" aria-hidden>
              {Array.from({ length: 10 }).map((_, idx) => (
                <div
                  key={`skeleton-${idx}`}
                  className="h-[108px] rounded-lg border border-border bg-muted/40 animate-pulse"
                />
              ))}
            </div>
          ) : (
          <div className="grid gap-2 [grid-template-columns:repeat(auto-fill,minmax(140px,1fr))] sm:[grid-template-columns:repeat(auto-fill,minmax(190px,1fr))]">
            {filteredProducts.map((product) => {
                const cat = product.category_id ? categoryById.get(product.category_id) : undefined
                const bg = categoryColor(cat, product.name)
                const inCart = cart.find((i) => i.product.id === product.id)?.quantity ?? 0
                const gate = canAddToCart(product, inCart, blockOversell)
                const disabled = !gate.ok
                const onHand = product.quantity_on_hand ?? 0
                const lowStock = product.kind === 'stock' && onHand <= (product.reorder_level ?? 0)
                const showTapFlash = lastTappedProductId === product.id
                return (
                  <button
                    key={product.id}
                    type="button"
                    disabled={disabled}
                    onClick={() => addToCart(product)}
                    className={cn(
                      'relative flex min-h-[108px] flex-col rounded-lg border border-border border-l-4 p-2 text-left shadow-sm select-none sm:p-2.5',
                      'transition-[transform,box-shadow,filter,outline] duration-100 [@media(prefers-reduced-motion:reduce)]:transition-none',
                      'active:scale-[0.96] active:brightness-[0.97] active:ring-2 active:ring-primary/40 active:ring-offset-2 [@media(prefers-reduced-motion:reduce)]:active:scale-100',
                      'disabled:pointer-events-none disabled:opacity-40'
                    )}
                    style={{
                      borderLeftColor: bg,
                      ...categorySurfaceStyle(bg),
                      ...(showTapFlash
                        ? {
                            outline: `2px solid ${bg}`,
                            outlineOffset: '3px',
                            zIndex: 1,
                          }
                        : {}),
                    }}
                  >
                    {product.kind === 'stock' && (
                      <span
                        aria-hidden
                        className={cn(
                          'pointer-events-none absolute left-1.5 top-1.5 z-10 rounded px-1.5 py-0.5 text-[10px] font-semibold tabular-nums',
                          onHand <= 0
                            ? 'bg-red-600 text-white'
                            : lowStock
                              ? 'bg-amber-500 text-white'
                              : 'bg-background/85 text-muted-foreground',
                        )}
                      >
                        {onHand <= 0 ? 'Out' : onHand}
                      </span>
                    )}
                    {tapPop.productId === product.id && (
                      <span
                        key={tapPop.nonce}
                        aria-hidden
                        className="counter-product-plus-one pointer-events-none absolute -top-2 right-2 z-20 rounded-full bg-orange-500 px-2 py-0.5 text-xs font-bold text-white shadow-md"
                      >
                        +1
                      </span>
                    )}
                    {/*
                     * Layout: image stacks above text on narrow (<sm) so the
                     * full card width is available for the product name and
                     * price; switches to image-left / text-right on sm+ where
                     * cards are wider. At narrow the 80px image was eating
                     * almost the entire card width, leaving the name + price
                     * truncated to ~32px.
                     */}
                    <div className="flex min-h-0 flex-1 flex-col gap-1.5 sm:flex-row sm:gap-2">
                      <div className="flex shrink-0 justify-center sm:block">
                        {product.image_url ? (
                          <img
                            src={product.image_url}
                            alt={product.name}
                            className="h-14 w-14 rounded-md object-cover bg-muted sm:h-20 sm:w-20"
                          />
                        ) : (
                          <ProductImagePlaceholder
                            className="h-14 w-14 sm:h-20 sm:w-20"
                            iconClassName="h-7 w-7 sm:h-10 sm:w-10"
                          />
                        )}
                      </div>
                      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden sm:h-20">
                        {/*
                         * Stay at text-sm at all breakpoints — text-base + 3-line
                         * clamp overflows the 80px text column by ~8px and clips
                         * the bottom of the third line (visible on "Grilled
                         * Chicken Breast", "Fresh Orange Juice"). text-sm
                         * leading-snug fits 3 lines (~58px) + price (~19px) inside
                         * the 80px column cleanly.
                         */}
                        <span className="min-h-0 flex-1 overflow-hidden text-left text-sm font-semibold leading-snug text-foreground line-clamp-2 sm:line-clamp-3">
                          {product.name}
                        </span>
                        <span className="mt-auto shrink-0 self-end text-xs font-normal tabular-nums text-primary sm:text-sm">
                          {formatCurrency(product.price)}
                        </span>
                      </div>
                    </div>
                  </button>
                )
              })}
          </div>
          )}
          </div>
        </div>
        </main>

        <div
          role="separator"
          aria-orientation="vertical"
          aria-valuenow={checkoutRailPx}
          aria-valuemin={COUNTER_RAIL_MIN}
          aria-valuemax={820}
          title="Drag to resize checkout panel. Double-click to reset width."
          style={{ touchAction: 'none' }}
          className="group relative z-10 hidden w-px shrink-0 cursor-col-resize touch-none select-none flex-col items-center bg-transparent md:flex"
          onPointerDown={beginCheckoutRailDrag}
          onDoubleClick={(e) => {
            e.preventDefault()
            resetCheckoutRailWidth()
          }}
        >
          <span className="sr-only">Resize checkout column</span>
          {/* The visible seam is the rail's own border-l; no separate track
              line here (keeps it a single thin hairline). The pill is the
              grab target. */}
          {/* Persistent grab handle ("blob") — the focal drag target. It
              receives pointer events so a grab anywhere on it bubbles to the
              separator's onPointerDown (currentTarget stays the separator, so
              pointer capture + snap + persistence are unchanged). */}
          <div
            aria-hidden
            className="absolute left-1/2 top-1/2 flex h-12 w-6 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-card shadow-md ring-1 ring-black/[0.02] transition-all group-hover:border-primary/60 group-hover:shadow-lg group-active:scale-95 group-active:border-primary"
          >
            <GripVertical className="pointer-events-none h-4 w-4 text-muted-foreground transition-colors group-hover:text-primary" />
          </div>
        </div>

        <aside
          aria-label="Ticket"
          style={isSplitLayout ? { width: checkoutRailPx, maxWidth: '100%' } : undefined}
          className={cn(
            'grid h-full min-h-0 min-w-0 shrink-0 grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden border-l border-border bg-gradient-to-b from-card via-card to-muted/[0.35]',
            !isSplitLayout && 'w-full max-w-none flex-1 min-h-0',
            mobileWorkspaceTab === 'menu' && 'hidden md:grid'
          )}
        >
          <div className="shrink-0 bg-card/90 backdrop-blur-sm">
            <TicketHeader
              orderType={orderType}
              orderTypesConfig={orderTypesRaw}
              existingOrder={existingOrder}
              customerName={customerName || existingOrder?.customer_name || ''}
              deliveryAddress={deliveryAddress}
              lifecycle={ticketLifecycle}
              onCloseOrder={handleCloseOrderFromHeader}
              hasLoadedOrder={Boolean(existingOrder || selectedOrder || continuingOrderId || cart.length > 0)}
            />
            <CounterRailToolbar
              orderType={orderType}
              existingOrder={existingOrder}
              continuingOrderId={continuingOrderId}
              checkoutOpen={checkoutOpen}
              customerName={customerName}
              setCustomerName={setCustomerName}
              customerEmail={customerEmail}
              setCustomerEmail={setCustomerEmail}
              customerPhone={customerPhone}
              setCustomerPhone={setCustomerPhone}
              guestBirthday={guestBirthday}
              setGuestBirthday={setGuestBirthday}
              deliveryAddress={deliveryAddress}
              setDeliveryAddress={setDeliveryAddress}
              savedAddresses={customerSavedAddresses}
              setSavedAddresses={setCustomerSavedAddresses}
              onGuestUpdated={handleGuestUpdated}
              onSelectHistoryOrder={handleSelectHistoryOrder}
              formatCurrency={formatCurrency}
              onCloseCheckout={() => {
                setCheckoutOpen(false)
                setSelectedOrder(null)
              }}
              onOpenSplit={
                checkoutOpen && payOrder && payAmount > 0
                  ? () => setSplitDialogOpen(true)
                  : undefined
              }
              splitActive={Boolean(splitState)}
              splitDisabledReason={splitDisabledReason}
            />
            {marketingOn && existingOrder?.customer_id && (
              <div className="border-b border-border/70 px-4 py-2.5 sm:px-5">
                <CounterLoyaltyChip
                  orderId={existingOrder.id}
                  customerId={existingOrder.customer_id}
                />
              </div>
            )}
            {existingOrder &&
              continuingOrderId &&
              !checkoutOpen &&
              orderPayableRemaining(existingOrder) > 0 &&
              cart.length > 0 && (
                <div className="border-b border-border/70 px-4 py-2.5 sm:px-5">
                  <Button
                    type="button"
                    className="h-10 w-full text-sm font-semibold"
                    onClick={() => selectPaymentOrder(existingOrder)}
                  >
                    Pay {formatCurrency(orderPayableRemaining(existingOrder))}
                  </Button>
                </div>
              )}
            </div>

            <div
              ref={railScrollRef}
              className="counter-checkout-rail-scroll flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto overscroll-contain px-4 py-4 sm:px-5 sm:py-5"
              aria-label="Order and checkout"
            >
              {checkoutOpen && selectedOrder && payOrder && (
                <section className="space-y-3">
                  {splitState && payAmount > 0 && !splitDialogOpen && (
                    <button
                      type="button"
                      className="flex w-full items-center justify-between rounded-lg border border-amber-400/60 bg-amber-50/60 px-3 py-2 text-left text-xs font-medium text-amber-800 transition-colors hover:bg-amber-100/60 dark:bg-amber-950/30 dark:text-amber-300 dark:hover:bg-amber-950/50"
                      onClick={() => setSplitDialogOpen(true)}
                    >
                      <span>
                        Split in progress — {splitPaidCount} of {splitState.guest_count} guests paid
                      </span>
                      <span className="font-semibold underline underline-offset-2">Resume</span>
                    </button>
                  )}
                  <CounterPaymentPanel
                    payOrder={payOrder}
                    paymentCheckoutIntent={paymentCheckoutIntent}
                    onPaymentIntent={onPaymentIntent}
                    paymentOrderFetching={paymentOrderFetching}
                    billableItems={billableItems}
                    paymentTotals={paymentTotals}
                    formatCurrency={formatCurrency}
                    payAmount={payAmount}
                    discountMode={discountMode}
                    onDiscountModeChange={setDiscountMode}
                    discountValue={discountValue}
                    onDiscountValueChange={setDiscountValue}
                    discountMutationPending={discountMutation.isPending}
                    onApplyDiscount={() => {
                      if (!selectedOrder) return
                      const raw = parseFloat(discountValue || '0')
                      if (Number.isNaN(raw) || raw < 0) return
                      if (discountMode === 'percent') {
                        if (raw === 0) {
                          discountMutation.mutate({ orderId: selectedOrder.id, discount_amount: 0 })
                        } else {
                          discountMutation.mutate({ orderId: selectedOrder.id, discount_percent: raw })
                        }
                      } else {
                        discountMutation.mutate({
                          orderId: selectedOrder.id,
                          discount_amount: raw,
                        })
                      }
                    }}
                    // F-MM-N7: `payArming` is the ≤1.5s wait for an in-flight
                    // tender switch. Folded in here so the tap gives feedback
                    // and can't be double-fired. Safe to disable on because it
                    // is capped and released in a finally — unlike binding
                    // straight to checkoutIntentMutation.isPending, which would
                    // ride the 30s axios timeout and strand the till.
                    processPaymentPending={processPaymentMutation.isPending || payArming}
                    onlineSubMethods={onlineSubMethods}
                    selectedOnlineSub={selectedOnlineSub}
                    onSelectOnlineSub={setSelectedOnlineSub}
                    onPrimaryPay={onPrimaryPay}
                    onCompleteZeroBalance={runCompleteZeroBalance}
                    partialPaymentBanner={partialPaymentBanner}
                    onDismissPartialPaymentBanner={() => setPartialPaymentBanner(null)}
                    onPrintProvisionalBill={handlePrintProvisionalBill}
                    printProvisionalDisabled={
                      payAmount <= 0 || hasPartialPayment || processPaymentMutation.isPending
                    }
                    printProvisionalPending={provisionalPrintBusy}
                    onPromoChanged={() => {
                      // A promo apply/remove (bank card promo or promo code)
                      // rewrites discount_amount + total server-side. Mirror the
                      // discountMutation success path exactly — panel query AND
                      // selectedOrder — or the pay tap sends the pre-discount
                      // total and the backend refuses it (F-MM-N10).
                      if (payOrder) void refreshOrderIntoTill(payOrder.id)
                    }}
                    onApplyItemDiscount={payOrder ? (itemId, next) => {
                      itemDiscountMutation.mutate({
                        orderId: payOrder.id,
                        itemId,
                        discount_amount: next?.mode === 'amount' ? next.value : 0,
                        discount_percent: next?.mode === 'percent' ? next.value : null,
                        // F-CTRL-N44: only present when the popover determined
                        // this comps the line to zero.
                        pin: next?.pin,
                      })
                    } : undefined}
                    cardPromoLabel={activeCardPromoLabel}
                    lineDiscountDisabled={hasActivePromo || hasActiveCardPromo}
                    lineDiscountDisabledHint={
                      hasActiveCardPromo
                        ? 'Remove the card promo to apply a line discount'
                        : 'Remove the promo to apply a line discount'
                    }
                    lineDiscountPending={itemDiscountMutation.isPending}
                    onEditDeliveryFee={payOrder?.order_type === 'delivery' ? onEditDeliveryFee : undefined}
                    deliveryDefaultFee={
                      payOrder ? getOrderTypePricing(payOrder.order_type, orderTypesRaw).deliveryFee : 0
                    }
                    deliveryFeePending={deliveryFeeMutation.isPending}
                  />

                  {cashModalOpen && paymentCheckoutIntent === 'cash' && (
                    <CashTenderPad
                      amountDue={payAmount}
                      received={cashReceived}
                      onReceivedChange={setCashReceived}
                      onCancel={() => {
                        setCashModalOpen(false)
                        setCashReceived('')
                      }}
                      onComplete={runCashPayment}
                      formatCurrency={formatCurrency}
                      processing={processPaymentMutation.isPending}
                    />
                  )}

                  {payPricingExtras && (
                    <SplitBillDialog
                      open={splitDialogOpen}
                      onOpenChange={setSplitDialogOpen}
                      order={payOrder}
                      pricing={pricing}
                      pricingExtras={payPricingExtras}
                      serverRemaining={payAmount}
                      processing={processPaymentMutation.isPending}
                      formatCurrency={formatCurrency}
                      onlineSubMethods={onlineSubMethods}
                      onOrderUpdated={handleSplitOrderUpdated}
                      onSetIntent={onPaymentIntent}
                      onPayGuest={handlePaySplitGuest}
                      onReprintGuest={handleReprintSplitGuest}
                    />
                  )}

                </section>
              )}

              <SentItemsSection
                order={existingOrder}
                categoryById={categoryById}
                categoryColor={categoryColor}
                formatCurrency={formatCurrency}
                defaultExpanded={existingItemsExpanded}
                onRequestVoid={existingOrder?.id ? (itemId, itemName, qty, unitPrice) => {
                  setVoidModal({ orderId: existingOrder.id, itemId, itemName, qty, unitPrice })
                } : undefined}
              />

              {offlineTarget && (
                <OfflineHeldTicket
                  order={offlineTarget}
                  nameFor={offlineProductName}
                  onClear={() => setOfflineTarget(null)}
                />
              )}

              <UnsentItemsSection
                cart={cart}
                categoryById={categoryById}
                categoryColor={categoryColor}
                formatCurrency={formatCurrency}
                continuing={appendingToLoadedOrder}
                onIncrement={addToCart}
                onDecrement={removeFromCart}
                onItemNotesChange={setItemNotes}
                flashProductId={lastCartFlashProductId}
                cartRowRefs={cartRowRefs}
                notes={orderNotes}
                onNotesChange={setOrderNotes}
                liveAnnouncementId={cartLiveAnnouncement.id}
                liveAnnouncementText={cartLiveAnnouncement.text}
              />
            </div>

            <div className="border-t border-border/90 bg-card/95 backdrop-blur-md">
              {/* F-FE-N30: orders held on this device. Renders nothing when the
                  queue is empty, which is the normal case — it appears only
                  during and just after an outage. Keyed on the pending count so
                  it re-reads immediately after a capture or a drain rather than
                  waiting for its poll. */}
              <div className="px-4 pt-2.5 sm:px-5">
                <OfflineOrdersTray
                  refreshKey={
                    offlineCapture.pendingCount +
                    offlineCapture.unreconciledCount +
                    offlineTrayNonce
                  }
                  selectedOpId={offlineTarget?.opId ?? null}
                  onSelect={handleSelectHeldOfflineOrder}
                />
              </div>
              {cart.length > 0 && !checkoutOpen && (
                <div className="flex items-center gap-2 px-4 pt-2.5 sm:px-5">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Tax preview
                  </span>
                  <div
                    role="tablist"
                    aria-label="Tax preview payment type"
                    className="ml-auto inline-flex rounded-md border border-border bg-muted/40 p-0.5"
                  >
                    {(
                      [
                        ['cash', 'Cash'],
                        ['card', 'Card'],
                        ['online', 'Online'],
                      ] as const
                    ).map(([k, label]) => {
                      const active = createCheckoutIntent === k
                      return (
                        <button
                          key={k}
                          type="button"
                          role="tab"
                          aria-selected={active}
                          onClick={() => setCreateCheckoutIntent(k)}
                          className={cn(
                            'h-7 rounded-[5px] px-3 text-xs font-medium transition-colors',
                            active
                              ? 'bg-background text-foreground shadow-sm'
                              : 'text-muted-foreground hover:text-foreground'
                          )}
                        >
                          {label}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}
              {existingOrder &&
              continuingOrderId &&
              !checkoutOpen &&
              orderPayableRemaining(existingOrder) > 0 &&
              cart.length === 0 ? (
                <div className="px-4 py-3 sm:px-5 sm:py-4">
                  <Button
                    type="button"
                    className="h-12 w-full text-base font-semibold sm:h-14 sm:text-lg"
                    onClick={() => selectPaymentOrder(existingOrder)}
                  >
                    <BadgeDollarSign className="mr-2 h-5 w-5" />
                    Pay {formatCurrency(orderPayableRemaining(existingOrder))}
                  </Button>
                </div>
              ) : (
                <ActionFooter
                  mode={cart.length > 0 ? 'compose' : checkoutOpen ? 'checkout' : 'idle'}
                  totals={
                    cart.length === 0
                      ? null
                      : {
                          subtotal: cartSubtotal,
                          tax: cartTotals.tax,
                          taxRate: cartTotals.taxRate,
                          delivery: cartTotals.delivery,
                          posFee: cartTotals.posFee,
                          total: cartTotals.total,
                        }
                  }
                  showCloseCheckoutWithCompose={checkoutOpen}
                  formatCurrency={formatCurrency}
                  deliveryEditable={orderType === 'delivery'}
                  deliveryOverridden={deliveryFeeOverride != null}
                  deliveryDefault={createOrderPricingExtras.deliveryFee}
                  deliveryPending={deliveryFeeMutation.isPending}
                  onEditDelivery={onEditDeliveryFee}
                  primaryLabel={
                    appendingToLoadedOrder ? 'Add items & fire KOT' : 'Place order & Fire'
                  }
                  onPrimary={handleSubmitCart}
                  primaryDisabled={
                    cart.length === 0 ||
                    submitCartMutation.isPending ||
                    // F-FE-N30: offline no longer means blocked. The button
                    // stays live when this terminal can genuinely capture and
                    // fire locally; otherwise the hint below says why not.
                    (!online && !offlineFireReady) ||
                    (!appendingToLoadedOrder && deliveryDetailsMissing) ||
                    (!appendingToLoadedOrder && guestPhoneError != null)
                  }
                  primaryPending={submitCartMutation.isPending}
                  disabledHint={
                    // F-CTRL-N37: adding to an existing tab now works offline
                    // too, so both paths ask the same predicate — there is no
                    // longer a separate "reconnect to add to the tab" case.
                    !online && cart.length > 0
                      ? // A refusal that no longer applies must not be quoted:
                        // when the fire will go through, the cashier gets the
                        // plain "held on this device" line instead of being
                        // told the number pool is empty.
                        offlineHintForRefusal(
                          offlineFireReady ? undefined : offlineCapture.capability.reason
                        )
                      : !appendingToLoadedOrder && deliveryDetailsMissing && cart.length > 0
                      ? 'Delivery needs a phone & address — open Guest details (red dot).'
                      : !appendingToLoadedOrder && guestPhoneError != null && cart.length > 0
                      ? `${guestPhoneError} Fix it in Guest details.`
                      : !checkoutOpen
                      ? cart.length === 0
                        ? 'Add items to enable the primary action.'
                        : undefined
                      : undefined
                  }
                  onCloseCheckout={() => {
                    setCheckoutOpen(false)
                    setSelectedOrder(null)
                  }}
                />
              )}
            </div>
        </aside>
        </div>
        </div>
      </div>
    </div>
  )
}
